import {
  Tabs,
  Badge,
  Card,
  Row,
  Col,
  Typography,
  Progress,
  Tag,
  Avatar,
  Button,
  Descriptions,
  Statistic,
  Input,
  message,
  Modal,
  Segmented,
  Space,
  Timeline,
  Select,
  Form,
  Spin,
  Table,
  Upload,
  Image,
  Popconfirm,
} from "antd";

import React, {
  useEffect,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useCurrency } from "../hooks/useCurrency";
import SimpleTable from "../components/simpleTable";
import {
  FileOutlined,
  UserOutlined,
  MailOutlined,
  IdcardOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  MessageOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  GiftOutlined,
  SearchOutlined,
  SyncOutlined,
  PhoneFilled,
  PhoneOutlined as PhoneIcon,
  AudioOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  StopOutlined,
  HistoryOutlined,
  UploadOutlined,
  PictureOutlined,
  BulbOutlined,
  WarningOutlined,
  ReadOutlined,
  RobotOutlined,
  FileSearchOutlined,
  BellOutlined,
} from "@ant-design/icons";
import { AccountsService } from "../utils/accountsService";
import { AppContext } from "../context/AppContext";
import { uploadFile } from "../utils/firebase";
import AIEligibilityService from "../utils/aiEligibilityService";
import BorrowerSummaryService from "../utils/borrowerSummaryService";
import AIEligibilityDisplay from "../components/AIEligibilityDisplay";
import moment from "moment";
import threeCXService from "../services/3cxService";
import threeCXWebClient from "../services/3cxWebClient";
import threeCXWebRTC from "../services/3cxWebRTC";

const { TabPane } = Tabs;
const { Title, Text, Paragraph } = Typography;

// Borrowers who signed up via the mobile app within the last 30 days get a
// "NEW" tag next to their name to flag fresh accounts.
const NEW_BORROWER_WINDOW_DAYS = 30;
const isNewBorrower = (signupDate) => {
  if (!signupDate) return false;
  const d = new Date(signupDate);
  if (Number.isNaN(d.getTime())) return false;
  const ageMs = Date.now() - d.getTime();
  return ageMs >= 0 && ageMs <= NEW_BORROWER_WINDOW_DAYS * 24 * 60 * 60 * 1000;
};

const Borrower = () => {
  const token = localStorage.getItem("token");
  const { company } = useContext(AppContext);
  const { formatWithCurrency } = useCurrency();
  const [user, setUser] = useState();
  const [filterType, setFilterType] = useState("all");
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationForm, setNotificationForm] = useState({
    title: "",
    body: "",
    kind: "info",
    loan: null,
  });
  const [showGiftCardModal, setShowGiftCardModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showPhoneLookupModal, setShowPhoneLookupModal] = useState(false);
  const [giftcards, setGiftcards] = useState([]);
  const [loadingGiftcards, setLoadingGiftcards] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupProvider, setLookupProvider] = useState("MTN");
  const [smsHistory, setSmsHistory] = useState([]);
  const [loadingSmsHistory, setLoadingSmsHistory] = useState(false);
  const [smsHistoryTotal, setSmsHistoryTotal] = useState(0);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [loadingNotificationHistory, setLoadingNotificationHistory] =
    useState(false);
  const [notificationHistoryTotal, setNotificationHistoryTotal] = useState(0);
  const [borrower, setBorrower] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const [aiEligibility, setAiEligibility] = useState(null);
  const [aiSummary, setAiSummary] = useState("");
  const [aiSummaryError, setAiSummaryError] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);

  // Call States (3CX Integration)
  const [showCallModal, setShowCallModal] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isCallRecording, setIsCallRecording] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callHistory, setCallHistory] = useState([]);
  const [loadingCallHistory, setLoadingCallHistory] = useState(false);
  const [callStatus, setCallStatus] = useState("idle"); // 'idle', 'dialing', 'ringing', 'connected', 'ended'
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [currentCallId, setCurrentCallId] = useState(null); // 3CX call ID
  const [is3CXEnabled, setIs3CXEnabled] = useState(false); // Track if 3CX is configured
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const aiService = new AIEligibilityService();
  const summaryServiceRef = useRef(new BorrowerSummaryService());
  const [form] = Form.useForm();
  const [deliveryForm] = Form.useForm();
  const [phoneLookupForm] = Form.useForm();
  const navigate = useNavigate();

  // State for editing borrower details
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [borrowerDetails, setBorrowerDetails] = useState({
    phone: "",
    address: "",
    employer: "",
    nrc: "",
    email: "",
  });

  // State for document management
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFileList, setUploadFileList] = useState([]);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);

  // State for document analysis
  const [documentAnalyzing, setDocumentAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState({});

  // State for editing next of kin information
  const [isEditingKin, setIsEditingKin] = useState(false);
  const [nextOfKin, setNextOfKin] = useState({
    name: "",
    relationship: "",
    phone: "",
  });

  const [smsData, setSmsData] = useState({
    message: "",
    sender_id: "", // Will be set from company context
    number: "260",
    chars: "0",
  });
  const formatKwacha = useCallback(
    (amount = 0, options = {}) =>
      formatWithCurrency(amount || 0, "ZMW", options),
    [formatWithCurrency],
  );

  // Set sender_id from company context when available
  useEffect(() => {
    if (company?.sender_id) {
      setSmsData((prev) => ({
        ...prev,
        sender_id: company.sender_id,
      }));
    } else {
      setSmsData((prev) => ({
        ...prev,
        sender_id: "Kwachaplus", // Fallback as specified
      }));
    }
  }, [company]);

  const fetchBorrowerSummary = useCallback(
    async (profile) => {
      if (!profile) return;
      setAiSummaryLoading(true);
      setAiSummaryError(null);

      try {
        const summaryText =
          await summaryServiceRef.current.generateBorrowerSummary(profile);
        setAiSummary(summaryText);
      } catch (error) {
        console.error("Borrower summary error:", error);
        const fallback = summaryServiceRef.current.getFallbackSummary(profile);
        setAiSummary(fallback);
        setAiSummaryError(
          error?.response?.data?.error?.message ||
            error.message ||
            "AI summary unavailable. Showing generated overview.",
        );
      } finally {
        setAiSummaryLoading(false);
      }
    },
    [summaryServiceRef],
  );

  // Check if 3CX is configured on component mount
  useEffect(() => {
    const useWebRTC = process.env.REACT_APP_3CX_USE_WEBRTC === "true";
    const useWebClient = process.env.REACT_APP_3CX_USE_WEB_CLIENT === "true";
    const isApiConfigured = threeCXService.isConfigured();
    const isWebClientConfigured = threeCXWebClient.isConfigured();
    const isWebRTCConfigured = threeCXWebRTC.isConfigured();

    let is3CXConfigured = false;
    let method = "";

    if (useWebRTC && isWebRTCConfigured) {
      is3CXConfigured = true;
      method = "WebRTC";
    } else if (useWebClient && isWebClientConfigured) {
      is3CXConfigured = true;
      method = "Web Client";
    } else if (isApiConfigured) {
      is3CXConfigured = true;
      method = "REST API";
    }

    setIs3CXEnabled(is3CXConfigured);
    if (is3CXConfigured) {
      console.log(`3CX ${method} integration is ready`);

      // Initialize WebRTC if enabled
      if (useWebRTC) {
        threeCXWebRTC.initialize().catch((err) => {
          console.error("WebRTC initialization failed:", err);
          message.error(
            "Failed to initialize 3CX calling. Please check your SIP credentials.",
          );
        });

        // Set up WebRTC event handlers
        threeCXWebRTC.on("progress", () => setCallStatus("ringing"));
        threeCXWebRTC.on("confirmed", () => setCallStatus("connected"));
        threeCXWebRTC.on("ended", () => {
          handleEndCall();
        });
        threeCXWebRTC.on("failed", (e) => {
          message.error("Call failed: " + (e?.cause || "Unknown error"));
          setCallStatus("idle");
          setIsCallActive(false);
        });
      }
    } else {
      console.log(
        "3CX not configured - falling back to traditional calling methods",
      );
    }

    return () => {
      // Cleanup WebRTC on unmount
      if (useWebRTC) {
        threeCXWebRTC.disconnect();
      }
    };
  }, []);

  // Poll 3CX call status when there's an active call
  useEffect(() => {
    let statusPollingInterval;

    if (currentCallId && is3CXEnabled && isCallActive) {
      // Poll every 3 seconds
      statusPollingInterval = setInterval(() => {
        monitor3CXCallStatus(currentCallId);
      }, 3000);
    }

    return () => {
      if (statusPollingInterval) {
        clearInterval(statusPollingInterval);
      }
    };
  }, [currentCallId, is3CXEnabled, isCallActive]);

  // Cleanup timer and USB connection on unmount
  useEffect(() => {
    return () => {
      if (window.callTimer) {
        clearInterval(window.callTimer);
        window.callTimer = null;
      }
      // Close USB port if open
      if (usbPort) {
        usbPort.close().catch(console.error);
      }
    };
  }, []);

  // Function to disconnect USB phone
  const disconnectUSBPhone = async () => {
    try {
      if (usbPort) {
        await usbPort.close();
        setUsbPort(null);
        setIsUsbConnected(false);
        message.success("USB phone disconnected");
      }
    } catch (error) {
      console.error("USB disconnection failed:", error);
      message.error("Failed to disconnect USB phone");
    }
  };

  // USB Phone connection state
  const [usbPort, setUsbPort] = useState(null);
  const [isUsbConnected, setIsUsbConnected] = useState(false);

  // Mobile device detection
  const isMobileDevice = () => {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      ) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
    );
  };

  // Check if Web Serial API is supported
  const isWebSerialSupported = () => {
    return "serial" in navigator && !isMobileDevice();
  };

  // Function to connect to USB phone
  const connectUSBPhone = async () => {
    try {
      // Check if on mobile device
      if (isMobileDevice()) {
        message.warning(
          "USB phone connection not supported on mobile devices. Calls will use your device's native dialer.",
        );
        return false;
      }

      // Check if Web Serial API is supported
      if (!isWebSerialSupported()) {
        message.error(
          "Web Serial API not supported. Please use Chrome/Edge browser on desktop.",
        );
        console.log("Web Serial API not supported");
        return false;
      }

      message.info("Please select your USB phone device from the list...");

      // Request USB serial port with better error handling
      let port;
      try {
        port = await navigator.serial.requestPort();
      } catch (error) {
        if (error.name === "NotFoundError") {
          message.error(
            "No USB phone device found. Please connect your phone via USB and try again.",
          );
        } else {
          message.error("Failed to access USB phone: " + error.message);
        }
        return false;
      }

      // Try different baud rates for compatibility
      const baudRates = [9600, 115200, 38400, 57600];
      let connected = false;

      for (const baudRate of baudRates) {
        try {
          await port.open({ baudRate });
          console.log(`Connected at ${baudRate} baud`);
          connected = true;
          break;
        } catch (error) {
          console.log(`Failed to connect at ${baudRate} baud:`, error);
          continue;
        }
      }

      if (!connected) {
        message.error(
          "Failed to connect to USB phone at any baud rate. Please check your connection.",
        );
        return false;
      }

      // Test the connection with a simple AT command
      try {
        const testResponse = await sendATCommand("AT");
        if (testResponse && testResponse.includes("OK")) {
          message.success("USB phone connected and responding to commands");
        } else {
          message.warning(
            "USB phone connected but may not support AT commands. Basic dialing will be attempted.",
          );
        }
      } catch (error) {
        console.log("AT test failed, but continuing:", error);
        message.warning(
          "USB phone connected. AT command support will be tested during dialing.",
        );
      }

      setUsbPort(port);
      setIsUsbConnected(true);
      return true;
    } catch (error) {
      console.error("USB connection failed:", error);

      if (error.name === "SecurityError") {
        message.error(
          "Permission denied. Please allow access to your USB phone device.",
        );
      } else if (error.name === "NotAllowedError") {
        message.error(
          "USB phone access was denied. Please try again and allow access.",
        );
      } else {
        message.error("Failed to connect to USB phone: " + error.message);
      }
      return false;
    }
  };

  // Function to send AT commands to phone with improved error handling
  const sendATCommand = async (command) => {
    if (!usbPort) return null;

    try {
      const writer = usbPort.writable.getWriter();
      const encoder = new TextEncoder();

      // Send AT command with proper termination
      await writer.write(encoder.encode(command + "\r\n"));
      writer.releaseLock();

      // Read response with timeout
      const reader = usbPort.readable.getReader();
      const decoder = new TextDecoder();

      let response = "";
      const timeout = setTimeout(() => {
        reader.cancel();
      }, 5000); // 5 second timeout

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          response += decoder.decode(value, { stream: true });

          // Check if we have a complete response (OK, ERROR, or specific response)
          if (
            response.includes("OK") ||
            response.includes("ERROR") ||
            response.includes("NO CARRIER")
          ) {
            break;
          }
        }
      } finally {
        clearTimeout(timeout);
        reader.releaseLock();
      }

      console.log(`AT Command: ${command}, Response: ${response}`);
      return response;
    } catch (error) {
      console.error("AT command failed:", error);
      return null;
    }
  };

  // Function to test USB phone connection
  const testUSBPhoneConnection = async () => {
    try {
      message.info("Testing USB phone connection...");

      if (!isUsbConnected || !usbPort) {
        message.warning("USB phone not connected. Attempting to connect...");
        const connected = await connectUSBPhone();
        if (!connected) {
          return false;
        }
      }

      // Test basic AT commands
      const commands = [
        { cmd: "AT", description: "Basic AT test" },
        { cmd: "AT+CGMI", description: "Manufacturer info" },
        { cmd: "AT+CGMM", description: "Model info" },
        { cmd: "AT+CPIN?", description: "SIM card status" },
      ];

      let testResults = [];

      for (const { cmd, description } of commands) {
        try {
          const response = await sendATCommand(cmd);
          if (response && response.includes("OK")) {
            testResults.push({ command: cmd, status: "OK", description });
          } else {
            testResults.push({ command: cmd, status: "FAILED", description });
          }
        } catch (error) {
          testResults.push({ command: cmd, status: "ERROR", description });
        }
      }

      // Show test results
      const successCount = testResults.filter((r) => r.status === "OK").length;

      if (successCount >= 2) {
        message.success(
          `USB phone test successful! ${successCount}/4 commands working.`,
        );
        console.log("USB Phone Test Results:", testResults);
      } else {
        message.warning(
          `USB phone test partial. ${successCount}/4 commands working. Basic dialing may still work.`,
        );
        console.log("USB Phone Test Results:", testResults);
      }

      return successCount >= 1; // At least basic AT command should work
    } catch (error) {
      console.error("USB phone test failed:", error);
      message.error("USB phone test failed: " + error.message);
      return false;
    }
  };

  // Function to get USB phone connection status and provide guidance
  const getUSBPhoneStatus = () => {
    if (isMobileDevice()) {
      return {
        status: "mobile",
        message: "On mobile device - will use native phone dialer",
        canConnect: false,
      };
    }

    if (!isWebSerialSupported()) {
      return {
        status: "unsupported",
        message: "Web Serial API not supported. Use Chrome/Edge on desktop.",
        canConnect: false,
      };
    }

    if (isUsbConnected && usbPort) {
      return {
        status: "connected",
        message: "USB phone connected and ready for calls",
        canConnect: false,
      };
    }

    return {
      status: "disconnected",
      message: "USB phone not connected. Click to connect your phone via USB.",
      canConnect: true,
    };
  };

  // Call-related functions with 3CX integration
  const handleStartCall = async () => {
    const borrowerPhone =
      borrowerDetails?.phone || borrower?.phone || user?.phone;

    if (!borrowerPhone) {
      message.error("No phone number available for this borrower");
      return;
    }

    console.log("Starting call to:", borrowerPhone);
    setCallStatus("dialing");
    setIsCallActive(true);

    // Clean and format phone number
    const cleanPhone = borrowerPhone.replace(/[^\d+]/g, "");

    // Ensure proper formatting for Zambia (+260)
    let formattedPhone = cleanPhone;
    if (formattedPhone.startsWith("0")) {
      formattedPhone = "+260" + formattedPhone.substring(1);
    } else if (
      !formattedPhone.startsWith("+260") &&
      !formattedPhone.startsWith("260")
    ) {
      formattedPhone = "+260" + formattedPhone;
    } else if (
      formattedPhone.startsWith("260") &&
      !formattedPhone.startsWith("+260")
    ) {
      formattedPhone = "+" + formattedPhone;
    }

    console.log("Formatted phone number:", formattedPhone);

    // Try 3CX first if enabled
    if (is3CXEnabled) {
      const useWebRTC = process.env.REACT_APP_3CX_USE_WEBRTC === "true";
      const useWebClient = process.env.REACT_APP_3CX_USE_WEB_CLIENT === "true";

      try {
        if (useWebRTC) {
          // Use WebRTC method (real calls via browser)
          message.loading("Initiating call via WebRTC...", 1);
          const result = await threeCXWebRTC.makeCall(formattedPhone);

          if (result.success) {
            message.success(`Calling ${formattedPhone} via WebRTC`);

            // Start call timer
            const startTime = Date.now();
            const timer = setInterval(() => {
              const elapsed = Math.floor((Date.now() - startTime) / 1000);
              setCallDuration(elapsed);
            }, 1000);
            window.callTimer = timer;

            return;
          }
        } else if (useWebClient) {
          // Use Web Client method (simpler, no API needed)
          message.info("Opening 3CX Web Client...");
          const result = await threeCXWebClient.makeCall(formattedPhone);

          if (result.success) {
            message.success(
              result.message || `3CX web client opened for ${formattedPhone}`,
              6,
            );

            // Start call timer for tracking
            const startTime = Date.now();
            const timer = setInterval(() => {
              const elapsed = Math.floor((Date.now() - startTime) / 1000);
              setCallDuration(elapsed);
            }, 1000);
            window.callTimer = timer;

            // Update status
            setTimeout(() => {
              setCallStatus("connected");
            }, 1000);

            return;
          }
        } else {
          // Use REST API method
          message.loading("Initiating call via 3CX API...", 1);
          const result = await threeCXService.makeCall(formattedPhone);

          if (result.success) {
            setCurrentCallId(result.callId);
            message.success(`Call initiated to ${formattedPhone}`);

            // Start call timer
            const startTime = Date.now();
            const timer = setInterval(() => {
              const elapsed = Math.floor((Date.now() - startTime) / 1000);
              setCallDuration(elapsed);
            }, 1000);
            window.callTimer = timer;

            // Update status to connected after brief delay
            setTimeout(() => {
              setCallStatus("ringing");
              setTimeout(() => {
                setCallStatus("connected");
              }, 2000);
            }, 1000);

            return;
          }
        }
      } catch (error) {
        console.error("3CX call failed:", error);
        message.warning("3CX call failed, trying fallback methods...");
        // Continue to fallback methods
      }
    }

    // Check if mobile device - use tel: protocol
    if (isMobileDevice()) {
      try {
        // Use a more specific tel: URL format to avoid Zoom conflicts
        const telUrl = `tel:${formattedPhone}`;

        message.info(`Opening ${formattedPhone} in your device's phone app...`);

        // Try to open native phone app with better error handling
        const link = document.createElement("a");
        link.href = telUrl;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Since we can't track the actual call on mobile, simulate call tracking
        const startTime = Date.now();
        const timer = setInterval(() => {
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          setCallDuration(elapsed);
        }, 1000);

        window.callTimer = timer;

        // Auto-update to connected status after a short delay
        setTimeout(() => {
          setCallStatus("connected");
        }, 2000);

        return;
      } catch (error) {
        console.error("Mobile call failed:", error);
        message.error("Failed to open phone dialer: " + error.message);
        setCallStatus("idle");
        setIsCallActive(false);
        return;
      }
    }

    // Desktop/USB phone functionality
    if (isUsbConnected && usbPort) {
      try {
        // Remove + and any non-digit characters for AT commands
        const atCommandPhone = formattedPhone.replace(/[^\d]/g, "");

        // Send AT command to dial number
        const response = await sendATCommand(`ATD${atCommandPhone};`);

        if (response && response.includes("OK")) {
          message.success(`Dialing ${formattedPhone} via USB phone...`);

          // Start call timer
          const startTime = Date.now();
          const timer = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            setCallDuration(elapsed);
          }, 1000);

          window.callTimer = timer;

          // Auto-update to connected status after a short delay
          setTimeout(() => {
            setCallStatus("connected");
          }, 3000);

          return;
        } else {
          message.error("USB phone did not respond correctly to dial command");
          setCallStatus("idle");
          setIsCallActive(false);
          return;
        }
      } catch (error) {
        console.error("USB call failed:", error);
        message.error("USB call failed: " + error.message);
        setCallStatus("idle");
        setIsCallActive(false);
        return;
      }
    } else {
      // Desktop fallback - if USB is not connected, try to connect first
      message.warning(
        "USB phone not connected. Attempting to connect first...",
      );

      const connected = await connectUSBPhone();
      if (connected) {
        // Retry the call with USB connection
        return handleStartCall();
      } else {
        // Final fallback - try to open system dialer
        try {
          message.info("Attempting to open system phone dialer...");

          // Use a more specific approach to avoid Zoom conflicts
          const telUrl = `tel:${formattedPhone}`;

          // Create a temporary link element
          const link = document.createElement("a");
          link.href = telUrl;
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Simulate call tracking for desktop
          const startTime = Date.now();
          const timer = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            setCallDuration(elapsed);
          }, 1000);

          window.callTimer = timer;

          setTimeout(() => {
            setCallStatus("connected");
          }, 2000);
        } catch (error) {
          console.error("System dialer failed:", error);
          message.error(
            "Cannot make call: Please connect your USB phone or use a supported phone dialer.",
          );
          setCallStatus("idle");
          setIsCallActive(false);
        }
        return;
      }
    }
  };

  const handleEndCall = async () => {
    console.log("Ending call");

    let callMethod = "System Dialer";
    const useWebRTC = process.env.REACT_APP_3CX_USE_WEBRTC === "true";

    // Try to end call via WebRTC
    if (is3CXEnabled && useWebRTC && threeCXWebRTC.isInCall()) {
      try {
        threeCXWebRTC.endCall();
        message.info("Call ended via WebRTC");
        callMethod = "3CX WebRTC";
      } catch (error) {
        console.error("WebRTC call end failed:", error);
        message.warning("Failed to end call via WebRTC");
      }
    }
    // Try to end call via 3CX REST API if we have a call ID
    else if (is3CXEnabled && currentCallId) {
      try {
        await threeCXService.endCall(currentCallId);
        message.info("Call ended via 3CX");
        callMethod = "3CX API";
        setCurrentCallId(null);
      } catch (error) {
        console.error("3CX call end failed:", error);
        message.warning("Failed to end call via 3CX");
      }
    }
    // End call via USB phone if connected
    else if (isUsbConnected && usbPort) {
      try {
        await sendATCommand("ATH"); // Hang up command
        message.info("Call ended via USB phone");
        callMethod = "USB Phone";
      } catch (error) {
        console.error("USB call end failed:", error);
      }
    } else if (isMobileDevice()) {
      callMethod = "Mobile Native Dialer";
    }

    // Clear timer
    if (window.callTimer) {
      clearInterval(window.callTimer);
      window.callTimer = null;
    }

    setCallStatus("ended");
    setIsCallActive(false);
    setIsCallRecording(false);

    // Add call to history
    const newCall = {
      id: Date.now(),
      phone: borrowerDetails?.phone || borrower?.phone || user?.phone,
      duration: callDuration,
      timestamp: new Date().toISOString(),
      status: "completed",
      recorded: recordingUrl !== null,
      recordingUrl: recordingUrl,
      method: callMethod,
    };
    setCallHistory((prev) => [newCall, ...prev]);

    // Show completion message
    message.success(
      `Call completed - Duration: ${formatCallDuration(callDuration)}`,
    );

    // Reset call state after a short delay
    setTimeout(() => {
      setCallDuration(0);
      setRecordingUrl(null);
      setCallStatus("idle");
      setShowCallModal(false);
    }, 2000);
  };

  const handleStartRecording = () => {
    console.log("Starting call recording");
    setIsCallRecording(true);
    message.info("Call recording started (Note: This is a UI simulation)");
  };

  const handleStopRecording = () => {
    console.log("Stopping call recording");
    setIsCallRecording(false);
    // Simulate recording URL for demo purposes
    setRecordingUrl(`https://recordings.example.com/RE${Date.now()}`);
    message.success("Call recording stopped");
  };

  const formatCallDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Monitor 3CX call status (poll for updates)
  const monitor3CXCallStatus = async (callId) => {
    if (!callId || !is3CXEnabled) return;

    try {
      const statusResult = await threeCXService.getCallStatus(callId);
      if (statusResult.success) {
        console.log("3CX Call Status:", statusResult.status);

        // Update UI based on 3CX call status
        switch (statusResult.status?.toLowerCase()) {
          case "dialing":
          case "initiated":
            setCallStatus("dialing");
            break;
          case "ringing":
            setCallStatus("ringing");
            break;
          case "answered":
          case "connected":
          case "active":
            setCallStatus("connected");
            break;
          case "ended":
          case "completed":
          case "failed":
            // Call ended, trigger cleanup
            handleEndCall();
            break;
          default:
            console.log("Unknown call status:", statusResult.status);
        }
      }
    } catch (error) {
      console.error("Failed to get 3CX call status:", error);
    }
  };

  // Function to open SMS modal with borrower's phone number
  const handleOpenSmsModal = () => {
    const borrowerPhone =
      borrowerDetails?.phone || borrower?.phone || user?.phone;

    //nsole.log("Original borrower phone:", borrowerPhone);

    // Format phone number to start with 260
    let formattedPhone = borrowerPhone || "260";
    if (formattedPhone && !formattedPhone.startsWith("260")) {
      if (formattedPhone.startsWith("0")) {
        // If starts with 0, replace 0 with 260
        formattedPhone = "260" + formattedPhone.substring(1);
      } else {
        // For other cases, prepend 260
        formattedPhone = "260" + formattedPhone;
      }
    }

    //onsole.log("Formatted phone for SMS:", formattedPhone);

    setSmsData((prev) => ({
      ...prev,
      number: formattedPhone,
    }));
    setShowSmsModal(true);
  };

  const handleSendSMS = async () => {
    // Validate phone number starts with 260
    if (!smsData.number.startsWith("260")) {
      message.error("Phone number must start with 260");
      return;
    }

    // Validate message is not empty
    if (!smsData.message.trim()) {
      message.error("Please enter a message");
      return;
    }

    try {
      const response = await axios.post(
        "https://app-express.net/kwachaplus/index.php",
        new URLSearchParams({
          did: user.id,
          company: company?.id || "1",
          message: smsData.message,
          sender_id: smsData.sender_id,
          number: smsData.number,
          chars: smsData.chars,
        }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        },
      );
      console.log(response.data);
      message.success("SMS sent successfully!");
      setShowSmsModal(false);
      // Reset message but keep number and sender_id
      setSmsData((prev) => ({
        ...prev,
        message: "",
        chars: "0",
      }));
      // Refresh SMS history after sending
      fetchSmsHistory();
    } catch (error) {
      console.error("SMS Error:", error);
      message.error("Failed to send SMS");
    }
  };

  const handleToggleNrcVerified = async (verified) => {
    const borrowerId = user?.user_id || borrower?.id || id;
    if (!borrowerId) {
      message.error("Borrower not loaded yet");
      return;
    }
    try {
      await axios.patch(
        `https://zedlenders.pickmesms.com/users/${borrowerId}`,
        { nrc_verified: verified },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Update local state so the badge reflects immediately. Defer to the
      // next frame so the Popconfirm tooltip finishes unmounting before the
      // layout shift  avoids the ResizeObserver loop warning Antd is prone
      // to during synchronous post-confirm state changes.
      requestAnimationFrame(() => {
        setUser((prev) => (prev ? { ...prev, nrc_verified: verified } : prev));
        if (setBorrower) {
          setBorrower((prev) =>
            prev ? { ...prev, nrc_verified: verified } : prev,
          );
        }
      });

      // Notify the borrower via the notifications  OneSignal Flow. Only
      // on positive verification (no point telling them about a flag-back).
      // Fire-and-forget  if it fails the verify itself still succeeded.
      if (verified) {
        try {
          await axios.post(
            "https://zedlenders.pickmesms.com/items/notifications",
            {
              recipient: borrowerId,
              company: company?.id || null,
              title: "NRC verified",
              body: "Your lender has verified your NRC. Your account is now fully set up.",
              kind: "success",
              read: false,
              status: "published",
            },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch (notifyError) {
          console.warn("Could not send NRC-verified notification", notifyError);
        }
      }

      message.success(
        verified ? "NRC marked as verified" : "NRC flagged for review",
      );
    } catch (error) {
      message.error(
        error?.response?.data?.errors?.[0]?.message ||
          "Could not update NRC status",
      );
    }
  };

  const handleSendNotification = async () => {
    const title = notificationForm.title.trim();
    const body = notificationForm.body.trim();
    if (!title) {
      message.error("Please enter a title");
      return;
    }
    if (!body) {
      message.error("Please enter a message body");
      return;
    }
    const recipientId = borrower?.id || id;
    if (!recipientId) {
      message.error("Borrower not loaded yet");
      return;
    }
    try {
      setSendingNotification(true);
      await axios.post(
        "https://zedlenders.pickmesms.com/items/notifications",
        {
          recipient: recipientId,
          company: company?.id || null,
          loan: notificationForm.loan || null,
          title,
          body,
          kind: notificationForm.kind,
          read: false,
          status: "published",
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      message.success("Notification sent");
      fetchNotificationHistory();
      setShowNotificationModal(false);
      setNotificationForm({ title: "", body: "", kind: "info", loan: null });
    } catch (error) {
      console.error("Notification error:", error);
      message.error(
        error?.response?.data?.errors?.[0]?.message ||
          "Failed to send notification",
      );
    } finally {
      setSendingNotification(false);
    }
  };

  // Function to fetch SMS history
  const fetchSmsHistory = async () => {
    if (!company?.id) return;

    const borrowerPhone =
      borrowerDetails?.phone || borrower?.phone || user?.phone;
    if (!borrowerPhone) return;

    console.log("Original phone for SMS history:", borrowerPhone);

    // Format phone number to start with 260 for API call
    let formattedPhone = borrowerPhone;
    if (formattedPhone && !formattedPhone.startsWith("260")) {
      if (formattedPhone.startsWith("0")) {
        // If starts with 0, replace 0 with 260
        formattedPhone = "260" + formattedPhone.substring(1);
      } else {
        // For other cases, prepend 260
        formattedPhone = "260" + formattedPhone;
      }
    }

    console.log("Formatted phone for SMS history API:", formattedPhone);

    try {
      setLoadingSmsHistory(true);
      const response = await axios.get(
        "https://app-express.net/kwachaplus/get_sms_history.php",
        {
          params: {
            phone_number: formattedPhone,
            company_id: company.id,
            limit: 50,
            offset: 0,
          },
        },
      );

      console.log("SMS History API response:", response.data);

      if (response.data.success) {
        setSmsHistory(response.data.data);
        setSmsHistoryTotal(response.data.total);
      } else {
        console.error("SMS History Error:", response.data.error);
      }
    } catch (error) {
      console.error("SMS History Fetch Error:", error);
    } finally {
      setLoadingSmsHistory(false);
    }
  };

  const fetchNotificationHistory = useCallback(async () => {
    const recipientId = borrower?.id || user?.id || id;
    if (!recipientId) return;

    try {
      setLoadingNotificationHistory(true);
      const response = await axios.get(
        "https://zedlenders.pickmesms.com/items/notifications",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            "filter[recipient][_eq]": recipientId,
            sort: "-date_created",
            limit: 100,
            fields:
              "id,title,body,kind,read,status,date_created,date_updated,loan",
            meta: "filter_count",
          },
        },
      );

      const rows = response.data?.data || [];
      setNotificationHistory(rows);
      setNotificationHistoryTotal(
        response.data?.meta?.filter_count ?? rows.length,
      );
    } catch (error) {
      console.error("Notification history fetch error:", error);
      message.error("Failed to load notification history");
    } finally {
      setLoadingNotificationHistory(false);
    }
  }, [borrower, user, id, token]);

  const riskFactors = [
    { label: "Payment History", value: "98%", status: "excellent" },
    { label: "Credit Utilization", value: "35%", status: "high" },
    { label: "Loan Diversity", value: "2 Types", status: "medium" },
    { label: "Recent Activity", value: "3 New Loans", status: "watch" },
  ];

  const columns = [
    {
      title: "Loan Amount",
      dataIndex: "amount",
      render: (amount) => formatKwacha(amount),
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: "Status",
      dataIndex: "loan_status",
      render: (status) => (
        <Tag color={statusMap[status]?.color || "default"}>
          {statusMap[status]?.text || "Unknown"}
        </Tag>
      ),
    },
    {
      title: "Progress",
      render: (_, record) => (
        <Progress
          percent={Math.round((record.paid_amount / record.amount) * 100)}
          size="small"
          status={record.loan_status === 8 ? "exception" : "normal"}
        />
      ),
    },
    {
      title: "Due Date",
      dataIndex: "next_payment_date",
      render: (date, record) => {
        // Check if loan is settled
        if (record.loan_status === 10) {
          return (
            <span style={{ color: "#52c41a", fontStyle: "italic" }}>
              N/A - Loan Settled
            </span>
          );
        }

        // Try next_payment_date first, then fall back to due_date
        const paymentDate = date || record.due_date;
        if (!paymentDate) return "N/A";
        try {
          const dateObj = new Date(paymentDate);
          return isNaN(dateObj.getTime())
            ? "N/A"
            : dateObj.toLocaleDateString();
        } catch (error) {
          return "N/A";
        }
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button type="link" onClick={() => navigate(`/loan/${record.id}`)}>
          View Details
        </Button>
      ),
    },
  ];

  const statusMap = {
    1: { text: "Pending Approval", color: "orange" },
    2: { text: "Under Review", color: "blue" },
    3: { text: "Approved", color: "cyan" },
    4: { text: "Rejected", color: "red" },
    5: { text: "Cancelled", color: "volcano" },
    6: { text: "Disbursed", color: "purple" },
    7: { text: "Active", color: "green" },
    8: { text: "Overdue", color: "red" },
    9: { text: "Defaulted", color: "magenta" },
    10: { text: "Closed", color: "gray" },
    11: { text: "Restructured", color: "gold" },
    12: { text: "App Application", color: "geekblue" },
  };

  const fetchGiftcards = async () => {
    setLoadingGiftcards(true);
    try {
      const response = await axios.get(
        "https://zedlenders.pickmesms.com/items/vouchers?filter[status][_eq]=not_assigned",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      setGiftcards(response.data.data || []);
    } catch (err) {
      console.error("Error fetching giftcards:", err);
      message.error("Failed to load gift cards");
    } finally {
      setLoadingGiftcards(false);
    }
  };

  const fetchDeliveries = async () => {
    setLoadingDeliveries(true);
    try {
      const response = await axios.get(
        "https://zedlenders.pickmesms.com/items/deliveries",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            // Filter deliveries for the current borrower
            "filter[recipient][_eq]": id,
          },
        },
      );

      const deliveriesData = response.data.data || [];
      setDeliveries(deliveriesData);
    } catch (err) {
      console.error("Error fetching deliveries:", err);
      message.error("Failed to load deliveries");
    } finally {
      setLoadingDeliveries(false);
    }
  };

  const handleAddDelivery = async (values) => {
    try {
      const payload = {
        ...values,
        recipient: id, // Set the recipient to the current borrower ID
        tracking_number: `TRK${Date.now()}`, // Auto-generate tracking number
      };

      // If status is delivered, set the actual delivery date
      if (values.status === "delivered" && !values.actual_delivery_date) {
        payload.actual_delivery_date = new Date().toISOString().split("T")[0];
      }

      const response = await axios.post(
        "https://zedlenders.pickmesms.com/items/deliveries",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data) {
        message.success("Delivery scheduled successfully");
        setShowDeliveryModal(false);
        deliveryForm.resetFields();
        // Refresh the deliveries list
        fetchDeliveries();
      }
    } catch (err) {
      console.error("Error scheduling delivery:", err);
      message.error("Failed to schedule delivery");
    }
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/users/${id}?fields=*.*`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setUser(response.data.data);
    } catch (err) {
      console.error("Error fetching borrower:", err);
    }
  };

  const handleRefreshSummary = () => {
    if (borrower) {
      fetchBorrowerSummary(borrower);
    }
  };

  useEffect(() => {
    const fetchBorrower = async () => {
      try {
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/users/${id}?fields=*.*,loans.*.*`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        setBorrower(response.data.data);

        // Initialize borrower details from fetched data
        if (response.data.data) {
          setBorrowerDetails({
            phone: response.data.data.phone || "",
            address: response.data.data.address || "",
            employer: response.data.data.employer || "",
            nrc: response.data.data.nrc || "",
            email: response.data.data.email || "",
          });

          // Initialize next of kin details
          setNextOfKin({
            name: response.data.data.next_kin_name || "",
            relationship: response.data.data.next_kin_relationship || "",
            phone: response.data.data.next_kin_phone || "",
          });
        }
      } catch (error) {
        console.error("Error fetching borrower:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBorrower();
  }, [id]);

  useEffect(() => {
    if (borrower) {
      fetchBorrowerSummary(borrower);
    }
  }, [borrower, fetchBorrowerSummary]);

  // Fetch SMS history when borrower data and company are available
  useEffect(() => {
    if (borrower && company?.id) {
      fetchSmsHistory();
    }
  }, [borrower, company]);

  useEffect(() => {
    if (borrower?.id || user?.id || id) {
      fetchNotificationHistory();
    }
  }, [borrower, user, id, fetchNotificationHistory]);

  // Handler for borrower details form changes
  const handleBorrowerDetailsChange = (field, value) => {
    setBorrowerDetails((prev) => ({ ...prev, [field]: value }));
  };

  // Handler for next of kin form changes
  const handleKinInfoChange = (field, value) => {
    setNextOfKin((prev) => ({ ...prev, [field]: value }));
  };

  // Handler for uploading new documents
  const handleUploadDocuments = async () => {
    if (uploadFileList.length === 0) {
      message.warning("Please select at least one document to upload");
      return;
    }

    try {
      setUploadingDocuments(true);

      // Upload files to Firebase
      const uploadPromises = uploadFileList.map((file) =>
        uploadFile(file.originFileObj, `borrower-${id}`),
      );

      const newDocumentUrls = await Promise.all(uploadPromises);

      // Get current documents and add new ones
      let currentDocs = [];
      try {
        if (Array.isArray(borrower.documents)) {
          currentDocs = borrower.documents;
        } else if (borrower.documents) {
          currentDocs = JSON.parse(borrower.documents);
        }
      } catch (e) {
        console.error("Error parsing documents:", e);
        currentDocs = [];
      }
      const updatedDocs = [...currentDocs, ...newDocumentUrls];

      // Update borrower record with new documents
      await axios.patch(
        `https://zedlenders.pickmesms.com/users/${id}`,
        { documents: updatedDocs },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Update local state
      setBorrower((prev) => ({
        ...prev,
        documents: updatedDocs,
      }));

      message.success(
        `Successfully uploaded ${newDocumentUrls.length} document(s)`,
      );
      setShowUploadModal(false);
      setUploadFileList([]);
    } catch (error) {
      console.error("Upload error:", error);
      message.error("Failed to upload documents");
    } finally {
      setUploadingDocuments(false);
    }
  };

  // Function to analyze document quality (simplified)
  const analyzeDocumentQuality = async (file) => {
    // Basic file validation
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
    ];

    if (!allowedTypes.includes(file.type)) {
      return { quality: "poor", confidence: 0, error: "Unsupported file type" };
    }

    if (file.size > maxSize) {
      return { quality: "poor", confidence: 0, error: "File too large" };
    }

    if (file.size < 10000) {
      // Less than 10KB might be too small
      return {
        quality: "poor",
        confidence: 0.3,
        error: "File might be too small",
      };
    }

    // Basic quality assessment based on file size and type
    let quality = "good";
    let confidence = 0.8;

    if (file.size < 50000) {
      // Less than 50KB
      quality = "fair";
      confidence = 0.6;
    }

    return { quality, confidence, classification: file.type };
  };

  // Function to extract text from documents (simulated OCR for NRC)
  const extractDocumentText = async (file) => {
    // Note: ML5.js doesn't have built-in OCR, but we can simulate
    // intelligent text extraction based on image analysis
    const analysis = await analyzeDocumentQuality(file);

    if (analysis.quality === "poor") {
      return {
        success: false,
        message: "Document quality too poor for text extraction",
        extractedData: {},
      };
    }

    // Simulate NRC text extraction based on file name and analysis
    const fileName = file.name.toLowerCase();
    const isNRC =
      fileName.includes("nrc") ||
      fileName.includes("id") ||
      fileName.includes("national");

    if (isNRC && analysis.confidence > 0.4) {
      // Simulate extracted NRC data (in a real implementation, you'd use a proper OCR service)
      return {
        success: true,
        message: "Text extraction completed",
        extractedData: {
          documentType: "NRC",
          quality: analysis.quality,
          confidence: analysis.confidence,
          // Note: In a real implementation, these would be actual OCR results
          suggestedFields: {
            nrc: "Auto-extracted (requires verification)",
            full_name: "Auto-extracted (requires verification)",
          },
        },
      };
    }

    return {
      success: true,
      message: "Document analyzed - no auto-extraction available for this type",
      extractedData: {
        documentType: "General Document",
        quality: analysis.quality,
        confidence: analysis.confidence,
      },
    };
  };

  // Enhanced upload handler with ML5.js analysis
  const handleDocumentAnalysis = async (file) => {
    try {
      setDocumentAnalyzing(true);

      // Analyze document quality
      const qualityAnalysis = await analyzeDocumentQuality(file);

      if (!qualityAnalysis) {
        return;
      }

      // Extract text if quality is good
      let textExtraction = null;
      if (qualityAnalysis.quality === "good") {
        textExtraction = await extractDocumentText(file);
      }

      // Store analysis results
      const fileId = file.uid || file.name;
      setAnalysisResults((prev) => ({
        ...prev,
        [fileId]: {
          quality: qualityAnalysis,
          textExtraction: textExtraction,
        },
      }));

      // Show analysis results to user
      if (qualityAnalysis.quality === "poor") {
        message.warning(
          `Document quality is poor (${Math.round(qualityAnalysis.confidence * 100)}% confidence). Consider retaking the photo.`,
        );
      } else if (qualityAnalysis.quality === "good") {
        message.success(
          `Document quality is good (${Math.round(qualityAnalysis.confidence * 100)}% confidence). Ready for processing.`,
        );

        if (textExtraction && textExtraction.success) {
          message.info(textExtraction.message);
        }
      }
    } catch (error) {
      console.error("Document analysis error:", error);
      message.error("Failed to analyze document");
    } finally {
      setDocumentAnalyzing(false);
    }
  };

  // Save borrower details to API
  const handleSaveBorrowerDetails = async () => {
    try {
      const trimmedEmail = borrowerDetails.email?.trim() || "";
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (trimmedEmail && !emailPattern.test(trimmedEmail)) {
        message.error("Please enter a valid email address.");
        return;
      }

      if (trimmedEmail) {
        const emailCheckResponse = await axios.get(
          `https://zedlenders.pickmesms.com/users?filter[email][_eq]=${encodeURIComponent(
            trimmedEmail,
          )}&fields=id,email,first_name,last_name`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const existingUser = emailCheckResponse.data.data?.find(
          (item) => String(item.id) !== String(id),
        );

        if (existingUser) {
          message.error(
            `This email already exists for ${existingUser.first_name} ${existingUser.last_name}.`,
          );
          return;
        }
      }

      const payload = {
        ...borrowerDetails,
        email: trimmedEmail || null,
      };

      const response = await axios.patch(
        `https://zedlenders.pickmesms.com/users/${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.data) {
        // Update local state
        setUser({
          ...user,
          ...payload,
        });
        setBorrowerDetails((prev) => ({
          ...prev,
          email: trimmedEmail,
        }));
        setIsEditingDetails(false);
        message.success("Borrower details updated successfully!");
      }
    } catch (error) {
      message.error(
        error.response?.data?.errors?.[0]?.message ||
          "Failed to update borrower details. Please try again.",
      );
    }
  };

  // Save next of kin details to API
  const handleSaveNextOfKin = async () => {
    try {
      const nextOfKinData = {
        next_kin_name: nextOfKin.name,
        next_kin_relationship: nextOfKin.relationship,
        next_kin_phone: nextOfKin.phone,
      };

      const response = await axios.patch(
        `https://zedlenders.pickmesms.com/users/${id}`,
        nextOfKinData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.data) {
        // Update local state
        setUser({
          ...user,
          ...nextOfKinData,
        });
        setIsEditingKin(false);
        message.success("Next of kin details updated successfully!");
      }
    } catch (error) {
      message.error(
        error.response?.data?.errors?.[0]?.message ||
          "Failed to update next of kin details. Please try again.",
      );
    }
  };

  const handlePhoneLookup = async () => {
    try {
      setLookupLoading(true);
      const values = await phoneLookupForm.validateFields();
      const result = await AccountsService.phoneNumberLookup(
        values.phone,
        lookupProvider,
      );
      setLookupResult(result);
      console.log("Phone lookup result:", result);
    } catch (error) {
      console.error("Phone lookup error:", error);
      // message.error("Failed to perform phone lookup. Please try again.");
    } finally {
      setLookupLoading(false);
    }
  };

  const checkGeneralEligibility = async () => {
    if (!borrower) return;

    setEligibilityLoading(true);
    try {
      const loans = borrower.loans || [];

      // Generic loan request for general eligibility
      const loanRequest = {
        amount: 10000, // Default assessment amount
        loan_type: "General Assessment",
        purpose: "General loan eligibility check",
      };

      const assessment = await aiService.assessEligibility(
        borrower,
        loans,
        loanRequest,
      );
      setAiEligibility(assessment);

      const recommendationColor =
        {
          APPROVE: "success",
          CONDITIONAL_APPROVE: "warning",
          DECLINE: "error",
        }[assessment.recommendation] || "info";

      message[recommendationColor](
        `General Eligibility: ${assessment.recommendation} (${assessment.confidence}% confidence)`,
      );

      // Show the modal with results
      setShowAiModal(true);
    } catch (error) {
      console.error("AI Eligibility Check Error:", error);
      message.error("Failed to perform eligibility assessment");
    } finally {
      setEligibilityLoading(false);
    }
  };

  const handleNewLoan = () => {
    // Navigate to add loan page with borrower ID in state
    navigate("/addLoan", {
      state: {
        selectedBorrower: {
          id: borrower.id,
          name: `${borrower.first_name} ${borrower.last_name}`,
          nrc: borrower.nrc,
        },
      },
    });
  };

  const assignGiftCard = async (values) => {
    try {
      // First update the giftcard to link it to the borrower
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/vouchers/${values.giftcard_id}`,
        {
          user_id: id,
          status: "active",
          date_issued: new Date().toISOString(),
          owner: user.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      message.success("Gift card assigned successfully");
      console.log("Gift card assigned successfully");
      setShowGiftCardModal(false);
      form.resetFields();

      // Refresh user data to show updated giftcards
      fetchData();
    } catch (err) {
      console.error("Error assigning giftcard:", err);
    }
  };

  useEffect(() => {
    fetchData();
    fetchDeliveries();
  }, [id, token]);

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );

  return (
    <div style={{ padding: 24 }}>
      <Card bordered={false}>
        {/* Header Section */}
        <Row gutter={24} align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Avatar size={64} icon={<UserOutlined />} />
          </Col>
          <Col flex="auto">
            <Title level={3} style={{ marginBottom: 0 }}>
              {user?.first_name} {user?.last_name}
              {isNewBorrower(user?.signup_date) ? (
                <Tag
                  color="green"
                  style={{
                    marginLeft: 10,
                    fontSize: 12,
                    verticalAlign: "middle",
                  }}
                >
                  NEW
                </Tag>
              ) : null}
            </Title>
            <Text type="danger">Ref : {user?.employee_number}</Text>
            {user?.signup_date ? (
              <div style={{ marginTop: 6 }}>
                {user?.nrc_verified ? (
                  <>
                    <Tag color="green" style={{ marginRight: 8 }}>
                      <CheckCircleOutlined /> NRC verified
                    </Tag>
                    <Popconfirm
                      title="Flag NRC for review?"
                      description="This moves the borrower back into the KYC review queue."
                      okText="Flag"
                      onConfirm={() => handleToggleNrcVerified(false)}
                    >
                      <Button type="link" size="small" style={{ padding: 0 }}>
                        Flag for review
                      </Button>
                    </Popconfirm>
                  </>
                ) : (
                  <>
                    <Tag color="orange" style={{ marginRight: 8 }}>
                      <WarningOutlined /> NRC needs review
                    </Tag>
                    <Popconfirm
                      title="Mark NRC verified?"
                      description="Confirm the NRC photo matches this borrower before approving."
                      okText="Mark verified"
                      onConfirm={() => handleToggleNrcVerified(true)}
                    >
                      <Button
                        type="primary"
                        size="small"
                        icon={<CheckCircleOutlined />}
                      >
                        Mark NRC verified
                      </Button>
                    </Popconfirm>
                  </>
                )}
              </div>
            ) : null}
          </Col>
          <Col>
            {/* <Button
              icon={<PhoneFilled />}
              onClick={handleOpenCallModal}
              type="primary"
              style={{
                marginRight: 8,
                backgroundColor: "#52c41a",
                borderColor: "#52c41a",
              }}
            >
              Make Call
            </Button> */}
            <Button
              icon={<MessageOutlined />}
              onClick={handleOpenSmsModal}
              type="primary"
              style={{ marginRight: 8 }}
            >
              Send SMS
            </Button>
            {/* <Button
              type="primary"
              onClick={() => window.open("https://online.543.co.zm/", "_blank")}
            >
              Payments
            </Button> */}
          </Col>
        </Row>

        {/* Key Information Row */}

        {/* Tabs Section */}
        <Tabs defaultActiveKey="1" tabPosition="top" type="card">
          <TabPane tab="📊 Overview" key="1">
            <Row gutter={24}>
              <Col span={16}>
                {/* MUTED: AI Borrower Snapshot - Uncomment to re-enable AI summary */}
                {/* <Card
                  title={
                    <span>
                      <BulbOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                      AI Borrower Snapshot
                    </span>
                  }
                  style={{
                    marginBottom: 24,
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                  extra={
                    <Button
                      type="link"
                      icon={<SyncOutlined />}
                      onClick={handleRefreshSummary}
                      disabled={!borrower}
                      loading={aiSummaryLoading}
                    >
                      Refresh
                    </Button>
                  }
                >
                  {aiSummaryLoading ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 12,
                        minHeight: 120,
                        padding: '24px',
                        background: 'linear-gradient(to right, #f0f5ff, #f9f0ff)',
                        borderRadius: 6
                      }}
                    >
                      <Spin size="default" />
                      <Text type="secondary" style={{ fontSize: 15 }}>
                        Analyzing borrower profile with AI...
                      </Text>
                    </div>
                  ) : (
                    <>
                      {aiSummaryError && (
                        <div style={{
                          padding: '12px 16px',
                          background: '#fff7e6',
                          border: '1px solid #ffd591',
                          borderRadius: 6,
                          marginBottom: 16
                        }}>
                          <WarningOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
                          <Text type="warning">
                            {aiSummaryError}
                          </Text>
                        </div>
                      )}
                      {aiSummary ? (
                        <div style={{
                          padding: '20px',
                          background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                          borderRadius: 8,
                          border: '1px solid #e8e8f8'
                        }}>
                          <div style={{ marginBottom: 12 }}>
                            <Text strong style={{
                              fontSize: 13,
                              color: '#8c8c8c',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Executive Summary
                            </Text>
                          </div>
                          <Paragraph style={{
                            marginBottom: 0,
                            fontSize: 15,
                            lineHeight: '1.8',
                            color: '#262626',
                            fontWeight: 400
                          }}>
                            <ReadOutlined style={{
                              marginRight: 8,
                              color: '#1890ff',
                              fontSize: 16
                            }} />
                            {aiSummary}
                          </Paragraph>
                          <div style={{
                            marginTop: 16,
                            paddingTop: 16,
                            borderTop: '1px solid #d9d9d9',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              <RobotOutlined style={{ marginRight: 6 }} />
                              Generated by AI Analysis
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {new Date().toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Text>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          padding: '32px',
                          textAlign: 'center',
                          background: '#fafafa',
                          borderRadius: 6,
                          border: '1px dashed #d9d9d9'
                        }}>
                          <FileSearchOutlined style={{
                            fontSize: 36,
                            color: '#bfbfbf',
                            marginBottom: 12
                          }} />
                          <div>
                            <Text type="secondary" style={{ display: 'block', fontSize: 14 }}>
                              AI summary will appear once borrower data is available.
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Click refresh to generate a new analysis.
                            </Text>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </Card> */}

                <Card title="Financial Overview" style={{ marginBottom: 24 }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic
                        title="Total Loan Amount (ZMW)"
                        value={
                          user?.loans?.reduce(
                            (sum, loan) => sum + (loan.amount || 0),
                            0,
                          ) || 0
                        }
                        formatter={(value) => formatKwacha(Number(value))}
                        valueStyle={{ color: "#078930" }}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Outstanding Balance (ZMW)"
                        value={
                          user?.loans?.reduce(
                            (sum, loan) =>
                              sum + (loan.amount - (loan.paid_amount || 0)),
                            0,
                          ) || 0
                        }
                        formatter={(value) => formatKwacha(Number(value))}
                        valueStyle={{ color: "#EF3340" }}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Payment Ratio"
                        value={
                          user?.loans?.length
                            ? Math.round(
                                (user.loans.reduce(
                                  (sum, loan) => sum + (loan.paid_amount || 0),
                                  0,
                                ) /
                                  user.loans.reduce(
                                    (sum, loan) => sum + (loan.amount || 0),
                                    0,
                                  )) *
                                  100,
                              )
                            : 0
                        }
                        suffix="%"
                        valueStyle={{ color: "#1890ff" }}
                      />
                      <Progress
                        percent={
                          user?.loans?.length
                            ? Math.round(
                                (user.loans.reduce(
                                  (sum, loan) => sum + (loan.paid_amount || 0),
                                  0,
                                ) /
                                  user.loans.reduce(
                                    (sum, loan) => sum + (loan.amount || 0),
                                    0,
                                  )) *
                                  100,
                              )
                            : 0
                        }
                        showInfo={false}
                        strokeColor="#1890ff"
                        style={{ marginTop: 8 }}
                      />
                    </Col>
                  </Row>
                </Card>

                <Card title="Loan Summary" style={{ marginBottom: 24 }}>
                  <Row gutter={[16, 16]}>
                    <Col span={6}>
                      <Card
                        bordered={false}
                        style={{ background: "#f0f5ff", borderRadius: 8 }}
                      >
                        <Statistic
                          title="Active Loans"
                          value={
                            user?.loans?.filter(
                              (loan) => loan.loan_status === 7,
                            ).length || 0
                          }
                          valueStyle={{ color: "#108ee9" }}
                        />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card
                        bordered={false}
                        style={{ background: "#fff7e6", borderRadius: 8 }}
                      >
                        <Statistic
                          title="Pending Approval"
                          value={
                            user?.loans?.filter(
                              (loan) => loan.loan_status === 1,
                            ).length || 0
                          }
                          valueStyle={{ color: "#fa8c16" }}
                        />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card
                        bordered={false}
                        style={{ background: "#f6ffed", borderRadius: 8 }}
                      >
                        <Statistic
                          title="Paid Off"
                          value={
                            user?.loans?.filter(
                              (loan) => loan.loan_status === 10,
                            ).length || 0
                          }
                          valueStyle={{ color: "#52c41a" }}
                        />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card
                        bordered={false}
                        style={{ background: "#fff1f0", borderRadius: 8 }}
                      >
                        <Statistic
                          title="Overdue & Defaulted"
                          value={
                            user?.loans?.filter(
                              (loan) =>
                                loan &&
                                (loan.loan_status === 8 ||
                                  loan.loan_status === 9),
                            ).length || 0
                          }
                          valueStyle={{ color: "#f5222d" }}
                        />
                      </Card>
                    </Col>
                  </Row>
                </Card>

                <Card title="Recent Activity" style={{ marginBottom: 24 }}>
                  <Timeline>
                    {[...(user?.loans || [])]
                      .sort(
                        (a, b) =>
                          new Date(b.approval_date || 0) -
                          new Date(a.approval_date || 0),
                      )
                      .slice(0, 5)
                      .map((loan) => (
                        <Timeline.Item
                          key={loan.id}
                          color={
                            loan.loan_status === 7
                              ? "green"
                              : loan.loan_status === 8
                                ? "red"
                                : "blue"
                          }
                        >
                          <div
                            onClick={() => navigate(`/loan/${loan.id}`)}
                            style={{
                              cursor: "pointer",
                              padding: "8px",
                              borderRadius: "4px",
                              transition: "background-color 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = "#f5f5f5";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "transparent";
                            }}
                            title={`Click to view loan details for Loan ${loan.id}`}
                          >
                            <p style={{ margin: "4px 0" }}>
                              <strong>Loan {loan.id}</strong> -{" "}
                              {statusMap[loan.loan_status]?.text || "Unknown"}
                            </p>
                            <p style={{ margin: "4px 0" }}>
                              Amount: {formatKwacha(loan.amount)}
                            </p>
                            <p style={{ margin: "4px 0" }}>
                              Date:{" "}
                              {new Date(
                                loan.approval_date || Date.now(),
                              ).toLocaleDateString()}
                            </p>
                          </div>
                        </Timeline.Item>
                      ))}

                    {(!user?.loans || user.loans.length === 0) && (
                      <Timeline.Item>
                        <p>No recent loan activity</p>
                      </Timeline.Item>
                    )}
                  </Timeline>
                </Card>
              </Col>

              <Col span={8}>
                <Card
                  style={{
                    marginBottom: 24,
                    borderRadius: 16,
                    background:
                      "linear-gradient(135deg, rgba(24, 144, 255, 0.08) 0%, rgba(82, 196, 26, 0.12) 100%)",
                    border: "1px solid rgba(24, 144, 255, 0.12)",
                  }}
                  bodyStyle={{ padding: 20 }}
                >
                  <div style={{ marginBottom: 16 }}>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#1f1f1f",
                        marginBottom: 4,
                      }}
                    >
                      Quick Actions
                    </div>
                    <div style={{ color: "#595959", fontSize: 13 }}>
                      Contact this borrower fast and take the next step.
                    </div>
                  </div>

                  <Button
                    block
                    size="large"
                    style={{ marginBottom: 10 }}
                    onClick={() => setShowNotificationModal(true)}
                    icon={<BellOutlined />}
                    type="primary"
                  >
                    Send App Notification
                  </Button>
                  <Button
                    block
                    size="large"
                    style={{ marginBottom: 10 }}
                    onClick={handleOpenSmsModal}
                    icon={<MessageOutlined />}
                  >
                    Send SMS
                  </Button>
                  <Button
                    block
                    size="large"
                    type="default"
                    onClick={handleNewLoan}
                  >
                    New Loan Application
                  </Button>
                </Card>

                <Card
                  title="Credit Assessment"
                  style={{
                    marginBottom: 24,
                    borderRadius: 16,
                    border: `1px solid ${getCreditScoreInfo(user?.credit_score).color}`,
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
                  }}
                  bodyStyle={{ padding: 20 }}
                  headStyle={{
                    background: `${getCreditScoreInfo(user?.credit_score).color}12`,
                    borderBottom: `1px solid ${getCreditScoreInfo(user?.credit_score).color}22`,
                    fontWeight: 700,
                  }}
                >
                  <div style={{ textAlign: "center", marginBottom: 18 }}>
                    <Progress
                      type="dashboard"
                      percent={Math.round(
                        ((user?.credit_score || 0) / 850) * 100,
                      )}
                      width={132}
                      strokeWidth={10}
                      format={() => (
                        <div>
                          <div style={{ fontSize: "22px", fontWeight: "bold" }}>
                            {`${user?.credit_score || 0}/850`}
                          </div>
                          <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                            Credit score
                          </div>
                        </div>
                      )}
                      strokeColor={getCreditScoreInfo(user?.credit_score).color}
                    />
                    <div style={{ marginTop: 12 }}>
                      <Tag
                        color={getCreditScoreInfo(user?.credit_score).color}
                        style={{ fontSize: 12, padding: "4px 10px", fontWeight: 600 }}
                      >
                        {getCreditScoreInfo(user?.credit_score).category}
                      </Tag>
                    </div>
                  </div>

                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Repayment History">
                      <Tag color="green">Good</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Loan Utilization">
                      <Tag color="blue">Moderate</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Risk Assessment">
                      <Tag
                        color={
                          user?.credit_score > 670
                            ? "green"
                            : user?.credit_score > 580
                              ? "orange"
                              : "red"
                        }
                      >
                        {user?.credit_score > 670
                          ? "Low Risk"
                          : user?.credit_score > 580
                            ? "Medium Risk"
                            : "High Risk"}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                <Card
                  title="Borrower Details"
                  style={{ marginBottom: 24 }}
                  extra={
                    <Button
                      icon={
                        isEditingDetails ? (
                          <SafetyCertificateOutlined />
                        ) : (
                          <UserOutlined />
                        )
                      }
                      onClick={() =>
                        isEditingDetails
                          ? handleSaveBorrowerDetails()
                          : setIsEditingDetails(true)
                      }
                    >
                      {isEditingDetails ? "Save Details" : "Edit Details"}
                    </Button>
                  }
                >
                  {isEditingDetails ? (
                    <Form layout="vertical">
                      <Row gutter={[16, 16]}>
                        <Col span={24} md={12}>
                          <Form.Item label="Email Address">
                            <Input
                              value={borrowerDetails.email}
                              onChange={(e) =>
                                handleBorrowerDetailsChange(
                                  "email",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter email address"
                              prefix={<MailOutlined />}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24} md={12}>
                          <Form.Item label="Phone Number">
                            <Input
                              value={borrowerDetails.phone}
                              onChange={(e) =>
                                handleBorrowerDetailsChange(
                                  "phone",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter phone number"
                              prefix={<PhoneOutlined />}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24} md={12}>
                          <Form.Item label="NRC Number">
                            <Input
                              value={borrowerDetails.nrc}
                              onChange={(e) =>
                                handleBorrowerDetailsChange(
                                  "nrc",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter NRC number"
                              prefix={<IdcardOutlined />}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24} md={12}>
                          <Form.Item label="Employer">
                            <Input
                              value={borrowerDetails.employer}
                              onChange={(e) =>
                                handleBorrowerDetailsChange(
                                  "employer",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter employer"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item label="Address">
                            <Input.TextArea
                              rows={3}
                              value={borrowerDetails.address}
                              onChange={(e) =>
                                handleBorrowerDetailsChange(
                                  "address",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter address"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                  ) : (
                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label="Full Name">
                        {user?.first_name} {user?.last_name}
                      </Descriptions.Item>
                      <Descriptions.Item label="NRC Number">
                        {borrowerDetails?.nrc || user?.nrc || "N/A"}
                        <Button
                          type="link"
                          icon={<IdcardOutlined />}
                          onClick={() => navigate(`/nrc-verification/${id}`)}
                          style={{ marginLeft: 8 }}
                        >
                          Verify
                        </Button>
                      </Descriptions.Item>
                      <Descriptions.Item label="Email">
                        {borrowerDetails?.email || user?.email || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Phone">
                        {borrowerDetails?.phone || user?.phone || "N/A"}
                        <Button
                          type="link"
                          icon={<SearchOutlined />}
                          onClick={() => {
                            phoneLookupForm.setFieldsValue({
                              phone:
                                borrowerDetails?.phone || user?.phone || "",
                            });
                            setShowPhoneLookupModal(true);
                          }}
                          style={{ marginLeft: 8 }}
                        >
                          Verify
                        </Button>
                      </Descriptions.Item>
                      <Descriptions.Item label="Address">
                        {borrowerDetails?.address || user?.address || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Employer">
                        {borrowerDetails?.employer || user?.employer || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Position">
                        {user?.position || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Status">
                        <Tag color={user?.active ? "green" : "red"}>
                          {user?.active ? "Active" : "Inactive"}
                        </Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                </Card>

                <Card
                  title="Next of Kin Information"
                  style={{ marginBottom: 24 }}
                  extra={
                    <Button
                      icon={
                        isEditingKin ? (
                          <SafetyCertificateOutlined />
                        ) : (
                          <UserOutlined />
                        )
                      }
                      onClick={() =>
                        isEditingKin
                          ? handleSaveNextOfKin()
                          : setIsEditingKin(true)
                      }
                    >
                      {isEditingKin ? "Save Details" : "Edit Details"}
                    </Button>
                  }
                >
                  {isEditingKin ? (
                    <Form layout="vertical">
                      <Row gutter={[16, 16]}>
                        <Col span={24} md={12}>
                          <Form.Item label="Full Name">
                            <Input
                              value={nextOfKin.name}
                              onChange={(e) =>
                                handleKinInfoChange("name", e.target.value)
                              }
                              placeholder="Enter full name"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24} md={12}>
                          <Form.Item label="Relationship">
                            <Input
                              value={nextOfKin.relationship}
                              onChange={(e) =>
                                handleKinInfoChange(
                                  "relationship",
                                  e.target.value,
                                )
                              }
                              placeholder="e.g. Spouse, Parent, Child, etc."
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item label="Phone Number">
                            <Input
                              value={nextOfKin.phone}
                              onChange={(e) =>
                                handleKinInfoChange("phone", e.target.value)
                              }
                              placeholder="Enter phone number"
                              prefix={<PhoneOutlined />}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                  ) : (
                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label="Full Name">
                        {nextOfKin.name ||
                          user?.next_kin_name ||
                          "Not specified"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Relationship">
                        {nextOfKin.relationship ||
                          user?.next_kin_relationship ||
                          "Not specified"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Phone Number">
                        {nextOfKin.phone ||
                          user?.next_kin_phone ||
                          "Not specified"}
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                </Card>

              </Col>
            </Row>
          </TabPane>

          <TabPane
            tab={
              <span>
                💵 Loans <Badge count={user?.loans?.length} />
              </span>
            }
            key="3"
          >
            <Card
              title="Loan Portfolio"
              extra={
                <Button onClick={handleNewLoan} type="primary">
                  New Loan
                </Button>
              }
            >
              <SimpleTable
                data={user?.loans}
                columns={columns}
                pagination={{ pageSize: 5 }}
                rowKey="id"
                scroll={{ x: true }}
              />
            </Card>
          </TabPane>

          {/* SMS History Tab */}
          <TabPane
            tab={
              <span>
                💬 SMS History <Badge count={smsHistoryTotal} />
              </span>
            }
            key="2"
          >
            <Card
              title="SMS History"
              extra={
                <Button
                  onClick={fetchSmsHistory}
                  loading={loadingSmsHistory}
                  icon={<SyncOutlined />}
                >
                  Refresh
                </Button>
              }
            >
              <Table
                dataSource={smsHistory}
                loading={loadingSmsHistory}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  total: smsHistoryTotal,
                  showSizeChanger: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} messages`,
                }}
                scroll={{ x: true }}
                columns={[
                  {
                    title: "Date & Time",
                    dataIndex: "sent_at",
                    key: "sent_at",
                    width: 150,
                    render: (text) => {
                      const date = moment.utc(text).local();
                      return (
                        <div>
                          <div>{date.format("MM/DD/YYYY")}</div>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            {date.format("HH:mm:ss")}
                          </Text>
                        </div>
                      );
                    },
                    sorter: (a, b) =>
                      moment(a.sent_at).unix() - moment(b.sent_at).unix(),
                    defaultSortOrder: "descend",
                  },
                  {
                    title: "Message",
                    dataIndex: "message",
                    key: "message",
                    width: 300,
                    render: (text) => (
                      <div
                        style={{
                          maxHeight: "60px",
                          overflow: "hidden",
                          lineHeight: "1.4",
                        }}
                      >
                        {text}
                      </div>
                    ),
                  },
                  {
                    title: "Status",
                    dataIndex: "status_text",
                    key: "status",
                    width: 120,
                    render: (text, record) => (
                      <Tag
                        color={
                          record.status_type === "success"
                            ? "green"
                            : record.status_type === "error"
                              ? "red"
                              : record.status_type === "processing"
                                ? "blue"
                                : record.status_type === "warning"
                                  ? "orange"
                                  : "default"
                        }
                      >
                        {text}
                      </Tag>
                    ),
                    filters: [
                      { text: "Delivered", value: "Delivered" },
                      { text: "Failed", value: "Failed" },
                      { text: "Pending", value: "Pending" },
                      { text: "Expired", value: "Expired" },
                      { text: "Rejected", value: "Rejected" },
                    ],
                    onFilter: (value, record) => record.status_text === value,
                  },
                  // {
                  //   title: "Characters",
                  //   dataIndex: "characters",
                  //   key: "characters",
                  //   width: 100,
                  //   align: "center",
                  // },
                  // {
                  //   title: "SMS ID",
                  //   dataIndex: "sms_id",
                  //   key: "sms_id",
                  //   width: 120,
                  //   render: (text) => (
                  //     <Text code style={{ fontSize: "11px" }}>
                  //       {text || "N/A"}
                  //     </Text>
                  //   ),
                  // },
                ]}
              />
              {smsHistory.length === 0 && !loadingSmsHistory && (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <MessageOutlined
                    style={{ fontSize: "48px", color: "#d9d9d9" }}
                  />
                  <div style={{ marginTop: "16px", color: "#999" }}>
                    No SMS messages found for this borrower
                  </div>
                </div>
              )}
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <BellOutlined /> Notifications{" "}
                <Badge count={notificationHistoryTotal} />
              </span>
            }
            key="notifications"
          >
            <Card
              title="Notification History"
              extra={
                <Space>
                  <Button
                    type="primary"
                    icon={<BellOutlined />}
                    onClick={() => setShowNotificationModal(true)}
                  >
                    Send Notification
                  </Button>
                  <Button
                    onClick={fetchNotificationHistory}
                    loading={loadingNotificationHistory}
                    icon={<SyncOutlined />}
                  >
                    Refresh
                  </Button>
                </Space>
              }
            >
              <Table
                dataSource={notificationHistory}
                loading={loadingNotificationHistory}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  total: notificationHistoryTotal,
                  showSizeChanger: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} notifications`,
                }}
                scroll={{ x: true }}
                columns={[
                  {
                    title: "Date & Time",
                    dataIndex: "date_created",
                    key: "date_created",
                    width: 160,
                    render: (text) => {
                      const date = moment.utc(text).local();
                      return (
                        <div>
                          <div>{date.format("MM/DD/YYYY")}</div>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            {date.format("HH:mm:ss")}
                          </Text>
                        </div>
                      );
                    },
                    sorter: (a, b) =>
                      moment(a.date_created).unix() -
                      moment(b.date_created).unix(),
                    defaultSortOrder: "descend",
                  },
                  {
                    title: "Title",
                    dataIndex: "title",
                    key: "title",
                    width: 220,
                    render: (text) => <Text strong>{text || "Untitled"}</Text>,
                  },
                  {
                    title: "Message",
                    dataIndex: "body",
                    key: "body",
                    width: 360,
                    render: (text) => (
                      <div
                        style={{
                          maxHeight: "60px",
                          overflow: "hidden",
                          lineHeight: "1.4",
                        }}
                      >
                        {text}
                      </div>
                    ),
                  },
                  {
                    title: "Kind",
                    dataIndex: "kind",
                    key: "kind",
                    width: 120,
                    render: (value) => {
                      const colorMap = {
                        info: "blue",
                        success: "green",
                        warning: "orange",
                        danger: "red",
                      };
                      return (
                        <Tag color={colorMap[value] || "default"}>
                          {(value || "info").toUpperCase()}
                        </Tag>
                      );
                    },
                  },
                  {
                    title: "Read",
                    dataIndex: "read",
                    key: "read",
                    width: 100,
                    render: (value) => (
                      <Tag color={value ? "green" : "gold"}>
                        {value ? "READ" : "UNREAD"}
                      </Tag>
                    ),
                  },
                  {
                    title: "Loan",
                    dataIndex: "loan",
                    key: "loan",
                    width: 110,
                    render: (value) => (value ? `#${value}` : "None"),
                  },
                  {
                    title: "Status",
                    dataIndex: "status",
                    key: "status",
                    width: 120,
                    render: (value) => (
                      <Tag color={value === "published" ? "green" : "default"}>
                        {(value || "unknown").toUpperCase()}
                      </Tag>
                    ),
                  },
                ]}
              />
              {notificationHistory.length === 0 &&
                !loadingNotificationHistory && (
                  <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <BellOutlined
                      style={{ fontSize: "48px", color: "#d9d9d9" }}
                    />
                    <div style={{ marginTop: "16px", color: "#999" }}>
                      No app notifications found for this borrower
                    </div>
                  </div>
                )}
            </Card>
          </TabPane>

          {/* Only show Gift Cards tab when company ID is 12 */}
          {company?.id === 12 && (
            <TabPane
              tab={
                <span>
                  Gift Cards{" "}
                  <Badge
                    count={user?.voucher?.length || 0}
                    style={{ backgroundColor: "#52c41a" }}
                  />
                </span>
              }
              key="4"
            >
              <Card
                title="Gift Card Transactions"
                extra={
                  <Button
                    type="primary"
                    icon={<GiftOutlined />}
                    onClick={() => {
                      fetchGiftcards();
                      setShowGiftCardModal(true);
                    }}
                  >
                    Assign Gift Card
                  </Button>
                }
              >
                {/* Summary Statistics */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={8}>
                    <Statistic
                      title="Total Gift Cards"
                      value={user?.vouchers?.length || 0}
                      prefix={<SafetyCertificateOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Total Amount (ZMW)"
                      value={
                        user?.vouchers?.reduce(
                          (sum, card) => sum + (parseFloat(card.amount) || 0),
                          0,
                        ) || 0
                      }
                      formatter={(value) => formatKwacha(Number(value))}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Active Cards"
                      value={
                        user?.vouchers?.filter(
                          (card) => card.status === "active",
                        ).length || 0
                      }
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Col>
                </Row>

                {/* Transaction Table */}
                <SimpleTable
                  data={user?.voucher || []}
                  columns={[
                    {
                      title: "Gift Card Number",
                      dataIndex: "card_number",
                      render: (text) => `**** **** **** ${text?.slice(-4)}`,
                      sorter: (a, b) =>
                        a.card_number?.localeCompare(b.card_number),
                    },
                    {
                      title: "Store",
                      dataIndex: "store",
                      render: (text) => text || "N/A",
                      sorter: (a, b) =>
                        (a.store || "").localeCompare(b.store || ""),
                    },
                    {
                      title: "Date Issued",
                      dataIndex: "date_issued",
                      render: (date) =>
                        date ? new Date(date).toLocaleDateString() : "N/A",
                      sorter: (a, b) =>
                        new Date(a.date_issued || 0) -
                        new Date(b.date_issued || 0),
                    },
                    {
                      title: "Amount",
                      dataIndex: "amount",
                      render: (amount) => formatKwacha(amount || 0),
                      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
                    },
                    {
                      title: "Status",
                      dataIndex: "status",
                      render: (status) => {
                        const statusConfig = {
                          active: { color: "green", label: "Active" },
                          used: { color: "volcano", label: "Redeemed" },
                          not_assigned: { color: "gray", label: "Inactive" },
                        };
                        const config = statusConfig[status] || {
                          color: "gray",
                          label: "Unknown",
                        };
                        return <Tag color={config.color}>{config.label}</Tag>;
                      },
                    },
                  ]}
                  pagination={{ pageSize: 5 }}
                  rowKey="id"
                />
              </Card>
            </TabPane>
          )}
          {/* Only show Courier/Delivery tab when company ID is 12 */}
          {company?.id === 12 && (
            <TabPane
              tab={
                <span>
                  Courier/Delivery{" "}
                  <Badge
                    count={
                      deliveries.filter(
                        (d) =>
                          d.status === "pending" || d.status === "in_transit",
                      ).length
                    }
                    style={{ backgroundColor: "#faad14" }}
                  />
                </span>
              }
              key="5"
            >
              <Card
                title="Delivery Schedule"
                extra={
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                      type="primary"
                      onClick={() => {
                        setShowDeliveryModal(true);
                        deliveryForm.setFieldsValue({
                          delivery_address:
                            borrowerDetails?.address || user?.address || "",
                        });
                      }}
                      style={{ marginRight: 16 }}
                    >
                      New Delivery
                    </Button>
                    <Segmented
                      options={["All", "Pending", "In Transit", "Delivered"]}
                      defaultValue="All"
                      onChange={(value) => {
                        const statusMap = {
                          All: "all",
                          Pending: "pending",
                          "In Transit": "in_transit",
                          Delivered: "delivered",
                        };
                        setFilterType(statusMap[value] || "all");
                      }}
                    />
                  </div>
                }
              >
                {/* Summary Statistics */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={8}>
                    <Statistic
                      title="Total Deliveries"
                      value={deliveries.length}
                      prefix={<EnvironmentOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Pending Deliveries"
                      value={
                        deliveries.filter((d) => d.status === "pending").length
                      }
                      valueStyle={{ color: "#faad14" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Delivered"
                      value={
                        deliveries.filter((d) => d.status === "delivered")
                          .length
                      }
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Col>
                </Row>

                {/* Timeline and Table View */}
                <Row gutter={24}>
                  <Col span={12}>
                    <Card title="Delivery Timeline" bordered={false}>
                      <Timeline mode="alternate">
                        {loadingDeliveries ? (
                          <Spin />
                        ) : (
                          deliveries
                            .filter((d) =>
                              filterType === "all"
                                ? true
                                : d.status === filterType,
                            )
                            .map((delivery) => {
                              const statusConfig = {
                                delivered: {
                                  color: "green",
                                  icon: <CheckCircleOutlined />,
                                },
                                pending: {
                                  color: "gold",
                                  icon: <ClockCircleOutlined />,
                                },
                                in_transit: {
                                  color: "blue",
                                  icon: <EnvironmentOutlined />,
                                },
                                cancelled: {
                                  color: "red",
                                  icon: <CloseCircleOutlined />,
                                },
                              };

                              const config = statusConfig[delivery.status] || {
                                color: "default",
                                icon: <ClockCircleOutlined />,
                              };

                              return (
                                <Timeline.Item
                                  key={delivery.id}
                                  color={config.color}
                                  label={new Date(
                                    delivery.scheduled_date,
                                  ).toLocaleDateString()}
                                  dot={config.icon}
                                >
                                  {delivery.commodity}
                                  <Tag
                                    color={config.color}
                                    style={{ marginLeft: 8 }}
                                  >
                                    {delivery.status?.toUpperCase()}
                                  </Tag>
                                  <br />
                                  <Text type="secondary">
                                    {delivery.delivery_address}
                                  </Text>
                                  <br />
                                  <Text type="secondary">
                                    Tracking: {delivery.tracking_number}
                                  </Text>
                                </Timeline.Item>
                              );
                            })
                        )}
                      </Timeline>
                    </Card>
                  </Col>

                  <Col span={12}>
                    <SimpleTable
                      loading={loadingDeliveries}
                      data={deliveries.filter((d) =>
                        filterType === "all" ? true : d.status === filterType,
                      )}
                      columns={[
                        {
                          title: "Scheduled Date",
                          dataIndex: "scheduled_date",
                          render: (date) => new Date(date).toLocaleDateString(),
                          sorter: (a, b) =>
                            new Date(a.scheduled_date) -
                            new Date(b.scheduled_date),
                        },
                        {
                          title: "Actual Delivery",
                          dataIndex: "actual_delivery_date",
                          render: (date) =>
                            date
                              ? new Date(date).toLocaleDateString()
                              : "Pending",
                          sorter: (a, b) =>
                            new Date(a.actual_delivery_date || 0) -
                            new Date(b.actual_delivery_date || 0),
                        },
                        {
                          title: "Commodity",
                          dataIndex: "commodity",
                          filters: [
                            { text: "Sugar", value: "Sugar" },
                            { text: "Mealie Meal", value: "Mealie Meal" },
                            { text: "Fertilizer", value: "Fertilizer" },
                          ],
                          onFilter: (value, record) =>
                            record.commodity === value,
                        },
                        {
                          title: "Address",
                          dataIndex: "delivery_address",
                        },
                        {
                          title: "Status",
                          dataIndex: "status",
                          render: (status) => {
                            const statusColors = {
                              delivered: "green",
                              pending: "gold",
                              in_transit: "blue",
                              cancelled: "red",
                            };
                            return (
                              <Tag color={statusColors[status] || "default"}>
                                {status?.toUpperCase() || "UNKNOWN"}
                              </Tag>
                            );
                          },
                          filters: [
                            { text: "Delivered", value: "delivered" },
                            { text: "Pending", value: "pending" },
                            { text: "In Transit", value: "in_transit" },
                            { text: "Cancelled", value: "cancelled" },
                          ],
                          onFilter: (value, record) => record.status === value,
                        },
                        // {
                        //   title: "Tracking #",
                        //   dataIndex: "tracking_number",
                        //   render: (num) => <a>{num}</a>,
                        // },
                      ]}
                      pagination={{ pageSize: 5 }}
                      rowKey="id"
                    />
                  </Col>
                </Row>
              </Card>
            </TabPane>
          )}

          {/* Registration Documents Tab */}
          <TabPane
            tab={
              <span>
                📄 Registration Documents{" "}
                <Badge
                  count={(() => {
                    try {
                      if (Array.isArray(borrower?.documents)) {
                        return borrower.documents.length;
                      } else if (borrower?.documents) {
                        return JSON.parse(borrower.documents).length;
                      }
                      return 0;
                    } catch (e) {
                      return 0;
                    }
                  })()}
                />
              </span>
            }
            key="documents"
          >
            <Card
              title="Registration Documents"
              extra={
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setShowUploadModal(true)}
                >
                  Upload Documents
                </Button>
              }
            >
              {(() => {
                let documents = [];
                try {
                  if (Array.isArray(borrower?.documents)) {
                    documents = borrower.documents;
                  } else if (borrower?.documents) {
                    documents = JSON.parse(borrower.documents);
                  }
                } catch (e) {
                  console.error("Error parsing documents for display:", e);
                  documents = [];
                }
                return documents && documents.length > 0 ? (
                  <Row gutter={[16, 16]}>
                    {documents.map((doc, index) => (
                      <Col xs={24} sm={12} md={8} lg={6} key={index}>
                        <Card
                          hoverable
                          cover={
                            doc.toLowerCase().includes(".pdf") ? (
                              <div
                                style={{
                                  height: 200,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#f5f5f5",
                                }}
                              >
                                <FileOutlined
                                  style={{ fontSize: 48, color: "#1890ff" }}
                                />
                              </div>
                            ) : (
                              <Image
                                src={doc}
                                alt={`Document ${index + 1}`}
                                style={{ height: 200, objectFit: "cover" }}
                              />
                            )
                          }
                          actions={[
                            <a
                              href={doc}
                              target="_blank"
                              rel="noopener noreferrer"
                              key="view"
                            >
                              View Full Size
                            </a>,
                          ]}
                        >
                          <Card.Meta
                            title={`Document ${index + 1}`}
                            description={
                              doc.toLowerCase().includes(".pdf")
                                ? "PDF Document"
                                : "Image Document"
                            }
                          />
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      backgroundColor: "#fafafa",
                      borderRadius: "8px",
                      border: "2px dashed #d9d9d9",
                    }}
                  >
                    <PictureOutlined
                      style={{
                        fontSize: 48,
                        color: "#bfbfbf",
                        marginBottom: 16,
                      }}
                    />
                    <Title level={4} style={{ color: "#bfbfbf" }}>
                      No Registration Documents
                    </Title>
                    <Text type="secondary">
                      No documents have been uploaded for this borrower yet.
                    </Text>
                    <br />
                    <Button
                      type="primary"
                      icon={<UploadOutlined />}
                      onClick={() => setShowUploadModal(true)}
                      style={{ marginTop: 16 }}
                    >
                      Upload First Document
                    </Button>
                  </div>
                );
              })()}
            </Card>
          </TabPane>
        </Tabs>
      </Card>
      <Modal
        title="Send in-app notification"
        open={showNotificationModal}
        onOk={handleSendNotification}
        confirmLoading={sendingNotification}
        okText="Send"
        onCancel={() => setShowNotificationModal(false)}
        width={520}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          This notification appears on the borrower's KwachaPlus mobile app
          under Notifications. It will not send SMS or email.
        </Text>

        <div style={{ marginBottom: 12 }}>
          <Text strong>Title</Text>
          <Input
            placeholder="e.g. Loan application approved"
            value={notificationForm.title}
            maxLength={120}
            onChange={(e) =>
              setNotificationForm((prev) => ({
                ...prev,
                title: e.target.value,
              }))
            }
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <Text strong>Message</Text>
          <Input.TextArea
            placeholder="What should the borrower see?"
            rows={4}
            maxLength={500}
            showCount
            value={notificationForm.body}
            onChange={(e) =>
              setNotificationForm((prev) => ({ ...prev, body: e.target.value }))
            }
          />
        </div>

        <Row gutter={12}>
          <Col span={12}>
            <Text strong>Kind</Text>
            <Select
              style={{ width: "100%" }}
              value={notificationForm.kind}
              onChange={(value) =>
                setNotificationForm((prev) => ({ ...prev, kind: value }))
              }
              options={[
                { label: "Info", value: "info" },
                { label: "Success", value: "success" },
                { label: "Warning", value: "warning" },
                { label: "Danger", value: "danger" },
              ]}
            />
          </Col>
          <Col span={12}>
            <Text strong>Related loan (optional)</Text>
            <Select
              style={{ width: "100%" }}
              allowClear
              placeholder="None"
              value={notificationForm.loan ?? undefined}
              onChange={(value) =>
                setNotificationForm((prev) => ({
                  ...prev,
                  loan: value ?? null,
                }))
              }
              options={(borrower?.loans || []).map((l) => ({
                label: `#${l.id}${l.loan_product?.loan_name ? ` · ${l.loan_product.loan_name}` : ""}`,
                value: l.id,
              }))}
            />
          </Col>
        </Row>
      </Modal>

      <Modal
        title="Compose SMS"
        open={showSmsModal}
        onOk={handleSendSMS}
        onCancel={() => setShowSmsModal(false)}
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Sender ID: </Text>
          <Tag color="blue">{smsData.sender_id || "Loading..."}</Tag>
        </div>

        <div style={{ marginBottom: 8 }}>
          <Text strong>Message:</Text>
          <div style={{ float: "right", color: "#999" }}>
            {smsData.message.length}/160 characters
          </div>
        </div>
        <Input.TextArea
          rows={4}
          value={smsData.message}
          onChange={(e) => {
            const message = e.target.value;
            setSmsData({
              ...smsData,
              message: message,
              chars: message.length.toString(),
            });
          }}
          placeholder="Type your message..."
          maxLength={160}
          showCount
        />

        <div style={{ marginTop: 16, marginBottom: 8 }}>
          <Text strong>Phone Number:</Text>
          <div style={{ fontSize: "12px", color: "#666", marginTop: 4 }}>
            <Text type="secondary">
              Note: Phone number must start with 260 (e.g., 260977123456)
            </Text>
          </div>
        </div>
        <Input
          value={smsData.number}
          onChange={(e) => setSmsData({ ...smsData, number: e.target.value })}
          prefix={<PhoneOutlined />}
          placeholder="260XXXXXXXXX"
        />
      </Modal>

      {/* Gift Card Assignment Modal */}
      <Modal
        title="Assign Gift Card to Borrower"
        visible={showGiftCardModal}
        onCancel={() => {
          setShowGiftCardModal(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Spin spinning={loadingGiftcards}>
          <Form form={form} layout="vertical" onFinish={assignGiftCard}>
            <Form.Item
              name="giftcard_id"
              label="Select Gift Card"
              rules={[
                {
                  required: true,
                  message: "Please select a gift card to assign",
                },
              ]}
            >
              <Select
                placeholder="Select a gift card"
                loading={loadingGiftcards}
                disabled={loadingGiftcards || giftcards.length === 0}
                showSearch
                optionFilterProp="children"
              >
                {giftcards.map((card) => (
                  <Select.Option key={card.id} value={card.id}>
                    Card: **** {card.card_number?.slice(-4) || "XXXX"} - Amount:
                    {formatKwacha(card.amount)} - Store: {card.store || "N/A"}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {giftcards.length === 0 && !loadingGiftcards && (
              <div
                style={{
                  textAlign: "center",
                  margin: "20px 0",
                  color: "#ff4d4f",
                }}
              >
                <Text type="danger">
                  No unassigned gift cards are available.
                </Text>
              </div>
            )}

            <Form.Item
              style={{ textAlign: "right", marginBottom: 0, marginTop: 24 }}
            >
              <Button
                style={{ marginRight: 8 }}
                onClick={() => {
                  setShowGiftCardModal(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                disabled={loadingGiftcards || giftcards.length === 0}
              >
                Assign Gift Card
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* New Delivery Modal */}
      <Modal
        title="Phone Number Verification"
        visible={showPhoneLookupModal}
        onCancel={() => {
          setShowPhoneLookupModal(false);
          setLookupResult(null);
          phoneLookupForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={phoneLookupForm}
          layout="vertical"
          onFinish={handlePhoneLookup}
        >
          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[{ required: true, message: "Please enter phone number" }]}
          >
            <Input placeholder="Enter phone number (e.g. 0762200229)" />
          </Form.Item>

          <Form.Item label="Network Provider">
            <Select
              value={lookupProvider}
              onChange={(value) => setLookupProvider(value)}
              style={{ width: "100%" }}
            >
              <Select.Option value="MTN">MTN</Select.Option>
              <Select.Option value="Airtel">Airtel</Select.Option>
              <Select.Option value="Zamtel">Zamtel</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ textAlign: "right" }}>
            <Button type="primary" htmlType="submit" loading={lookupLoading}>
              Verify Number
            </Button>
          </Form.Item>
        </Form>

        {lookupResult && (
          <div style={{ marginTop: 16 }}>
            <Card title="Verification Result">
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Account">
                  {lookupResult.account}
                </Descriptions.Item>
                <Descriptions.Item label="First Name">
                  {lookupResult.firstName}
                </Descriptions.Item>
                <Descriptions.Item label="Last Name">
                  {lookupResult.lastName}
                </Descriptions.Item>
                <Descriptions.Item label="Full Name">
                  {lookupResult.fullName}
                </Descriptions.Item>
                <Descriptions.Item label="Provider">
                  {lookupResult.provider}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag
                    color={
                      lookupResult.status === "successful" ? "green" : "red"
                    }
                  >
                    {lookupResult.status}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </div>
        )}
      </Modal>

      <Modal
        title="Schedule New Delivery"
        visible={showDeliveryModal}
        onCancel={() => {
          setShowDeliveryModal(false);
          deliveryForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={deliveryForm}
          layout="vertical"
          onFinish={handleAddDelivery}
        >
          <Form.Item
            name="delivery_address"
            label="Delivery Address"
            rules={[
              { required: true, message: "Please enter delivery address" },
            ]}
          >
            <Input placeholder="Enter delivery address" />
          </Form.Item>

          <Form.Item
            name="scheduled_date"
            label="Scheduled Date"
            rules={[
              { required: true, message: "Please select a scheduled date" },
            ]}
          >
            <Input type="date" min={new Date().toISOString().split("T")[0]} />
          </Form.Item>

          <Form.Item
            name="commodity"
            label="Commodity"
            rules={[{ required: true, message: "Please enter commodity" }]}
          >
            <Select placeholder="Select commodity">
              <Select.Option value="Sugar">Sugar</Select.Option>
              <Select.Option value="Mealie Meal">Mealie Meal</Select.Option>
              <Select.Option value="Fertilizer">Fertilizer</Select.Option>
              <Select.Option value="Rice">Rice</Select.Option>
              <Select.Option value="Assorted Groceries">
                Assorted Groceries
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            initialValue="pending"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="in_transit">In Transit</Select.Option>
              <Select.Option value="delivered">Delivered</Select.Option>
              <Select.Option value="cancelled">Cancelled</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={() => {
                setShowDeliveryModal(false);
                deliveryForm.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Schedule Delivery
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Call Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <PhoneFilled style={{ marginRight: 8, color: "#52c41a" }} />
            <span>
              Call {user?.first_name} {user?.last_name}
            </span>
            <Tag color="green" style={{ marginLeft: 8 }}>
              {borrowerDetails?.phone ||
                borrower?.phone ||
                user?.phone ||
                "No phone number"}
            </Tag>
          </div>
        }
        open={showCallModal}
        onCancel={() => setShowCallModal(false)}
        footer={null}
        width={500}
        centered
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          {/* Device Connection Status */}
          <div
            style={{
              marginBottom: 16,
              padding: 8,
              backgroundColor: "#f5f5f5",
              borderRadius: 4,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text strong>
                {is3CXEnabled
                  ? "3CX Status:"
                  : isMobileDevice()
                    ? "Mobile Device:"
                    : "USB Phone Status:"}
              </Text>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: is3CXEnabled
                      ? "#52c41a"
                      : isMobileDevice()
                        ? "#52c41a"
                        : isUsbConnected
                          ? "#52c41a"
                          : "#ff4d4f",
                  }}
                />
                <Text
                  type={
                    is3CXEnabled
                      ? "success"
                      : isMobileDevice()
                        ? "success"
                        : isUsbConnected
                          ? "success"
                          : "danger"
                  }
                >
                  {is3CXEnabled
                    ? "Connected & Ready"
                    : isMobileDevice()
                      ? "Native Dialer Ready"
                      : isUsbConnected
                        ? "Connected"
                        : "Disconnected"}
                </Text>
                {!isMobileDevice() &&
                  (!isUsbConnected ? (
                    <Button
                      size="small"
                      type="primary"
                      onClick={connectUSBPhone}
                      style={{ marginLeft: 8 }}
                    >
                      Connect USB Phone
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      type="default"
                      onClick={disconnectUSBPhone}
                      style={{ marginLeft: 8 }}
                    >
                      Disconnect
                    </Button>
                  ))}
              </div>
            </div>
            {isMobileDevice() && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Calls will open in your device's native phone app
                </Text>
              </div>
            )}
          </div>

          {/* Call Status Display */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              {callStatus === "idle" && (
                <PhoneIcon style={{ color: "#52c41a" }} />
              )}
              {callStatus === "dialing" && (
                <PhoneIcon style={{ color: "#faad14" }} />
              )}
              {callStatus === "ringing" && (
                <PhoneIcon style={{ color: "#1890ff" }} />
              )}
              {callStatus === "connected" && (
                <PhoneFilled style={{ color: "#52c41a" }} />
              )}
              {callStatus === "ended" && (
                <PhoneIcon style={{ color: "#ff4d4f" }} />
              )}
            </div>

            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ fontSize: 18 }}>
                {callStatus === "idle" && "Ready to call"}
                {callStatus === "dialing" && "Dialing..."}
                {callStatus === "ringing" && "Ringing..."}
                {callStatus === "connected" && "Connected"}
                {callStatus === "ended" && "Call ended"}
              </Text>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">
                {borrowerDetails?.phone ||
                  borrower?.phone ||
                  user?.phone ||
                  "No phone number"}
              </Text>
            </div>

            {isCallActive && (
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: 24, color: "#52c41a" }}>
                  {formatCallDuration(callDuration)}
                </Text>
              </div>
            )}
          </div>

          {/* Call Controls */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 16,
              marginBottom: 24,
            }}
          >
            {!isCallActive ? (
              <Button
                type="primary"
                size="large"
                shape="circle"
                icon={<PhoneFilled />}
                onClick={handleStartCall}
                style={{
                  backgroundColor: "#52c41a",
                  borderColor: "#52c41a",
                  width: 60,
                  height: 60,
                  fontSize: 24,
                }}
              />
            ) : (
              <>
                {/* Recording Control - Only show on desktop */}
                {!isMobileDevice() && (
                  <Button
                    type={isCallRecording ? "primary" : "default"}
                    size="large"
                    shape="circle"
                    icon={
                      isCallRecording ? <StopOutlined /> : <AudioOutlined />
                    }
                    onClick={
                      isCallRecording
                        ? handleStopRecording
                        : handleStartRecording
                    }
                    style={{
                      backgroundColor: isCallRecording ? "#ff4d4f" : "#faad14",
                      borderColor: isCallRecording ? "#ff4d4f" : "#faad14",
                      color: "white",
                      width: 50,
                      height: 50,
                    }}
                  />
                )}

                {/* End Call */}
                <Button
                  type="primary"
                  size="large"
                  shape="circle"
                  icon={<PhoneFilled />}
                  onClick={handleEndCall}
                  style={{
                    backgroundColor: "#ff4d4f",
                    borderColor: "#ff4d4f",
                    width: 60,
                    height: 60,
                    fontSize: 24,
                  }}
                />
              </>
            )}
          </div>

          {/* Call Status Text */}
          <div style={{ marginBottom: 16 }}>
            {callStatus === "idle" && (
              <Text type="secondary">
                {isMobileDevice()
                  ? "Click the call button to open your phone's dialer"
                  : "Click the call button to start calling"}
              </Text>
            )}
            {callStatus === "dialing" && (
              <Text style={{ color: "#faad14" }}>
                {isMobileDevice()
                  ? `Opening phone dialer for ${user?.first_name}...`
                  : `Connecting to ${user?.first_name}...`}
              </Text>
            )}
            {callStatus === "ringing" && (
              <Text style={{ color: "#1890ff" }}>Phone is ringing...</Text>
            )}
            {callStatus === "connected" && (
              <div>
                <Text style={{ color: "#52c41a" }}>
                  {isMobileDevice()
                    ? "Call initiated (tracking in progress)"
                    : "Call in progress"}
                </Text>
                <br />
                {!isMobileDevice() && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {isCallRecording
                      ? "🔴 Recording"
                      : "Click record to start recording"}
                  </Text>
                )}
                {isMobileDevice() && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Call recording not available on mobile
                  </Text>
                )}
              </div>
            )}
            {callStatus === "ended" && (
              <Text style={{ color: "#ff4d4f" }}>Call completed</Text>
            )}
          </div>
        </div>
      </Modal>

      {/* Call History Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <HistoryOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            <span>Call History</span>
          </div>
        }
        open={false} // This would be controlled by a separate state
        onCancel={() => {}} // Handler for call history modal
        footer={null}
        width={700}
      >
        <Table
          dataSource={callHistory}
          loading={loadingCallHistory}
          rowKey="id"
          columns={[
            {
              title: "Date & Time",
              dataIndex: "timestamp",
              render: (timestamp) => {
                const date = new Date(timestamp);
                return (
                  <div>
                    <div>{date.toLocaleDateString()}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {date.toLocaleTimeString()}
                    </Text>
                  </div>
                );
              },
              sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
              defaultSortOrder: "descend",
            },
            {
              title: "Phone Number",
              dataIndex: "phone",
              render: (phone) => <Text code>{phone}</Text>,
            },
            {
              title: "Duration",
              dataIndex: "duration",
              render: (duration) => formatCallDuration(duration),
              sorter: (a, b) => a.duration - b.duration,
            },
            {
              title: "Status",
              dataIndex: "status",
              render: (status) => (
                <Tag
                  color={
                    status === "completed"
                      ? "green"
                      : status === "failed"
                        ? "red"
                        : "blue"
                  }
                >
                  {status.toUpperCase()}
                </Tag>
              ),
            },
            {
              title: "Method",
              dataIndex: "method",
              render: (method) => (
                <Tag color={method === "USB Phone" ? "purple" : "blue"}>
                  {method || "System Dialer"}
                </Tag>
              ),
            },
            {
              title: "Recording",
              dataIndex: "recorded",
              render: (recorded, record) =>
                recorded ? (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => window.open(record.recordingUrl, "_blank")}
                  >
                    Play Recording
                  </Button>
                ) : (
                  <Text type="secondary">No recording</Text>
                ),
            },
          ]}
          pagination={{ pageSize: 10 }}
        />
      </Modal>

      {/* AI Eligibility Assessment Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <SafetyCertificateOutlined
              style={{ marginRight: 8, color: "#1890ff" }}
            />
            <span>AI Loan Eligibility Assessment</span>
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {user?.first_name} {user?.last_name}
            </Tag>
          </div>
        }
        open={showAiModal}
        onCancel={() => setShowAiModal(false)}
        footer={[
          <Button
            key="recheck"
            onClick={checkGeneralEligibility}
            loading={eligibilityLoading}
          >
            Recheck Eligibility
          </Button>,
          <Button
            key="close"
            type="primary"
            onClick={() => setShowAiModal(false)}
          >
            Close
          </Button>,
        ]}
        width={900}
        style={{ top: 20 }}
      >
        <AIEligibilityDisplay
          eligibility={aiEligibility}
          onRecheck={checkGeneralEligibility}
          loading={eligibilityLoading}
        />
      </Modal>

      {/* Upload Documents Modal */}
      <Modal
        title="Upload Registration Documents"
        open={showUploadModal}
        onCancel={() => {
          setShowUploadModal(false);
          setUploadFileList([]);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowUploadModal(false);
              setUploadFileList([]);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="upload"
            type="primary"
            loading={uploadingDocuments}
            onClick={handleUploadDocuments}
            disabled={uploadFileList.length === 0}
            icon={<UploadOutlined />}
          >
            Upload Documents
          </Button>,
        ]}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Upload registration documents for this borrower. Supported formats:
            Images (JPG, PNG) and PDFs.
          </Text>
          <div
            style={{
              marginTop: 8,
              padding: 8,
              backgroundColor: "#f6ffed",
              borderRadius: 4,
            }}
          >
            <Text type="success">
              ✅ Document validation ready! Your uploads will be automatically
              checked for basic quality.
            </Text>
          </div>
          {documentAnalyzing && (
            <div
              style={{
                marginTop: 8,
                padding: 8,
                backgroundColor: "#fff7e6",
                borderRadius: 4,
              }}
            >
              <Text type="warning">🔍 Analyzing document... Please wait.</Text>
            </div>
          )}
        </div>

        <Upload
          beforeUpload={() => false}
          listType="picture-card"
          multiple
          accept="image/*,.pdf,.doc,.docx"
          fileList={uploadFileList}
          onChange={({ fileList }) => {
            setUploadFileList(fileList);

            // Analyze new files with ML5.js
            const newFiles = fileList.filter(
              (file) =>
                !analysisResults[file.uid || file.name] &&
                file.originFileObj &&
                file.type.startsWith("image/"),
            );

            newFiles.forEach((file) => {
              if (file.originFileObj) {
                handleDocumentAnalysis(file.originFileObj);
              }
            });
          }}
          maxCount={10}
        >
          {uploadFileList.length < 10 && (
            <div>
              <PictureOutlined />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          )}
        </Upload>

        {uploadFileList.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Text strong>Selected files: {uploadFileList.length}</Text>

            {/* Display analysis results */}
            {Object.keys(analysisResults).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text strong>AI Analysis Results:</Text>
                {uploadFileList.map((file) => {
                  const fileId = file.uid || file.name;
                  const analysis = analysisResults[fileId];

                  if (!analysis || !file.type.startsWith("image/")) return null;

                  return (
                    <div
                      key={fileId}
                      style={{
                        marginTop: 8,
                        padding: 8,
                        border: "1px solid #d9d9d9",
                        borderRadius: 4,
                        backgroundColor:
                          analysis.quality.quality === "good"
                            ? "#f6ffed"
                            : "#fff2f0",
                      }}
                    >
                      <Text strong>{file.name}</Text>
                      <br />
                      <Text
                        type={
                          analysis.quality.quality === "good"
                            ? "success"
                            : "danger"
                        }
                      >
                        Quality: {analysis.quality.quality.toUpperCase()}(
                        {Math.round(analysis.quality.confidence * 100)}%
                        confidence)
                      </Text>
                      {analysis.quality.classification && (
                        <>
                          <br />
                          <Text type="secondary">
                            Detected: {analysis.quality.classification}
                          </Text>
                        </>
                      )}
                      {analysis.textExtraction &&
                        analysis.textExtraction.success && (
                          <>
                            <br />
                            <Text type="info">
                              📄 {analysis.textExtraction.message}
                            </Text>
                          </>
                        )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

// Helper functions
const getCreditScoreInfo = (score = 0) => {
  if (score >= 800) return { category: "Exceptional", color: "#006400" };
  if (score >= 740) return { category: "Very Good", color: "#228B22" };
  if (score >= 670) return { category: "Good", color: "#32CD32" };
  if (score >= 580) return { category: "Fair", color: "#FFD700" };
  return { category: "Poor", color: "#FF4500" };
};

const getRiskColor = (status) => {
  switch (status) {
    case "excellent":
      return "green";
    case "high":
      return "red";
    case "medium":
      return "orange";
    case "watch":
      return "purple";
    default:
      return "gray";
  }
};

export default Borrower;
